import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { VisuallyHidden as RadixVisuallyHidden } from '@radix-ui/react-visually-hidden';
import { DialogRoot, DialogPortal, DialogOverlay, DialogContent, DialogTitle } from '../../../../../a11y/Dialog';
import {
    SwipePlayIcon,
    SwipeSoundOffIcon,
    SwipeSoundOnIcon,
    SwipeCloseIcon,
    SwipeCommentIcon,
    SwipeShareIcon,
    SwipeTikTokIcon,
    ViewsIcon,
    SwipeVerifiedIcon,
    SwipeCoachHandIcon,
    SwipeLikeIcon,
    ChevronUpIcon,
    ChevronDownIcon,
} from '../../../../../Utils/icons';
import { useGdpr } from '../../../../../Frontend/Hooks/GdprContext';
import { createTikTokAdapterChain, safeUrl } from './swipeAdapters';
import { desiredIndices, syncSlotWindow } from './slotWindow';
import { clampDragOffset, decideSnap, gestureNow, isTap } from './swipeDrag';
import { emitSwipeAnalytics } from './swipeAnalytics';
import SwipeSlot from './SwipeSlot';
import './SwipeView.css';

// Constants are all normative — spec §3.1/§3.2/§5.2, no plugin-local tuning.
const SLOT_COUNT = 3; // windowRadius(1) * 2 + 1 — spec §4.4.
/*
 * ── Wheel tuning (rule 1.16) ────────────────────────────────────────────────
 *
 * Adopted from the reference, replacing a single-event threshold of 25 with a
 * 550ms cooldown. The difference is what the model IS, not the numbers.
 *
 * The old rule fired on any one event whose `deltaY` cleared 25 and then locked
 * for 550ms. That reads a trackpad badly in both directions: a gentle two-finger
 * nudge sends a stream of 4-8px events that never individually clear 25 and so
 * navigates nothing, while a flick sends one 300px event followed by a long
 * inertia tail whose events keep arriving after the cooldown expires — so a
 * single flick advanced two or three slides.
 *
 * The reference ACCUMULATES instead: sum `deltaY` until it clears 40, fire once,
 * then refuse everything until the gesture has actually ended. A gesture ends
 * when the events stop for longer than 160ms, which is the whole trick — it is
 * what tells an inertia tail apart from a new deliberate scroll, and neither a
 * per-event threshold nor a fixed cooldown can distinguish those at all.
 *
 * The 450ms floor between navigations survives on top, so a fast alternating
 * scroll cannot outrun the track's own 320ms transition.
 */
const WHEEL_ACCUM_PX = 40;
const WHEEL_GESTURE_END_MS = 160;
const WHEEL_MIN_INTERVAL_MS = 450;
const NAV_COOLDOWN_MS = 420;
const START_TIMEOUT_MS = 4000;
const TRANSITION_MS = 320;
/*
 * The mute label's beat, measured off the reference (spec §2.4): it slides out
 * 350ms after a slide becomes active and retracts at 4500ms. Both are the
 * design's own numbers rather than tuned here — the label is meant to arrive
 * just after the video rather than with it, and to leave before it becomes
 * furniture.
 */
const MUTE_HINT_IN_MS = 350;
const MUTE_HINT_OUT_MS = 4500;
/* How long a share toast stays up — the reference's own 1800ms. */
const TOAST_MS = 1800;
/*
 * ── Pull-to-close (rule 1.16) ───────────────────────────────────────────────
 *
 * A downward drag past 140px, released on the FIRST slide, closes the viewer —
 * the platform gesture for "get me out of here", and the reference's own number
 * (measured 130 → no, 145 → yes).
 *
 * Read against the RAW finger travel rather than the clamped track offset, and
 * that distinction is the whole reason this composes with the strict clamp
 * instead of fighting it. At slide 0 `clampDragOffset` pins downward travel to
 * zero, so the track does not move at all and the clamp's feel is untouched; the
 * gesture is still fully measurable because the finger's own delta is.
 *
 * Restricted to slide 0 on purpose: mid-track a downward drag means "previous",
 * and there is no ambiguity to resolve because that slide exists.
 */
const PULL_TO_CLOSE_PX = 140;
/*
 * The pause glyph's dissolve, and the one number JS has to own (rule 1.10).
 *
 * The bloom-IN is pure CSS — it fires on mount and needs no help. The
 * dissolve-OUT needs the element to survive its own animation, so the component
 * has to know how long that is. Kept in step with `sbtt-swipeview-flash-out` in
 * SwipeView.css by name in both comments; they are two halves of one 100ms.
 */
const FLASH_OUT_MS = 100;

/*
 * ── The first-run coach overlay (rule 1.9) ──────────────────────────────────
 *
 * The design keeps exactly TWO hints, and this is the second (the first is the
 * mute button's label). Aman's own framing is the scoping argument: "I know I
 * added four instructions... trying to minimize what we tell them", on the
 * reasoning that the interaction vocabulary is already learned from the real
 * apps. So there is no pause hint, no desktop swipe hint, and nothing else.
 *
 * The three numbers, all the design's:
 *   - ARM at 350ms. The dismiss listeners are not live at mount, because a
 *     touchstart belonging to the tap that OPENED the viewer would otherwise
 *     arrive first and dismiss the overlay before it was ever seen.
 *   - AUTO-DISMISS at 3200ms, so it goes on its own for a visitor who neither
 *     scrolls nor taps.
 *   - ONCE EVER, in `localStorage`, under a plugin-namespaced key so the
 *     TikTok and Instagram viewers do not consume each other's first run on a
 *     site running both.
 */
const COACH_ARM_MS = 350;
const COACH_AUTO_DISMISS_MS = 3200;
const COACH_STORAGE_KEY = 'sbtt-swipeview-coach-seen';

/*
 * "Is this a touch layout?" — the INVERSE of the desktop capability query, and
 * the default matters.
 *
 * With no `matchMedia` at all (this project's jsdom, notably) this answers
 * FALSE, i.e. do not show the overlay. That is the opposite default from the
 * intro card this replaced, which kept its fuller copy when the gate was
 * unknown on the reasoning that dropping guidance is the worse failure. The
 * reasoning inverts here because the failure inverts: an un-gated card showed a
 * phone an "Esc to Close" row it could not honour, whereas an un-gated coach
 * overlay tells a desktop visitor to "swipe up" over a viewer they are driving
 * with a mouse. Withholding a hint is recoverable; a wrong instruction is not.
 *
 * ONE GATE, ONE ANSWER: this is the only thing that decides whether the overlay
 * exists, and there is deliberately no `@media` rule hiding it as well. A CSS
 * gate would be a second, independently-evaluated source of truth — and when
 * the two disagreed (no `matchMedia`, exactly the case above) the overlay would
 * be mounted-but-hidden, silently consuming the once-ever flag for a visitor who
 * never saw it. That is the same class of bug the removed intro card's
 * `hintVariantClass` note warned about.
 */
/**
 * The desktop capability query, in ONE place.
 *
 * `SwipeView.css` gates the desktop layout on exactly this string, and two
 * behaviours now read it from JS as well (the coach overlay, and the share
 * button's desktop/touch arms). Duplicating the literal is how a JS gate and a
 * CSS rule quietly stop agreeing, so there is one constant and a test asserts
 * the stylesheet still uses it verbatim.
 */
export const DESKTOP_POINTER_MEDIA = '(hover: hover) and (pointer: fine)';

function isTouchLayout() {
    try {
        if (!window.matchMedia) return false;
        return !window.matchMedia(DESKTOP_POINTER_MEDIA).matches;
    } catch (e) {
        return false;
    }
}

/*
 * The other side of the same question, named for the thing it decides.
 *
 * Evaluated at CLICK TIME rather than sampled into state, because the answer
 * can change under the visitor (a tablet gaining a trackpad, a window dragged
 * to another display) and a share button that acts on a stale reading is worse
 * than one that asks again — it is one call per tap.
 *
 * It inherits `isTouchLayout()`'s default: with no `matchMedia` at all this
 * answers DESKTOP, which for share means clipboard-and-toast. That is the arm
 * that works everywhere and never opens a sheet nobody asked for, so it is the
 * right thing to do when the environment will not say.
 */
function isDesktopPointer() {
    return !isTouchLayout();
}

/*
 * Has this visitor already been shown the overlay?
 *
 * Every `localStorage` access is wrapped, and not defensively: Safari's private
 * mode throws on WRITE, and a browser configured to block site data throws on
 * READ. An exception here would take the whole viewer down on open, so the
 * failure mode is chosen deliberately — a read that throws answers "already
 * seen", suppressing the overlay rather than showing it on every single open to
 * someone whose browser cannot remember that it did.
 */
function coachAlreadySeen() {
    try {
        return window.localStorage.getItem(COACH_STORAGE_KEY) === '1';
    } catch (e) {
        return true;
    }
}

function markCoachSeen() {
    try {
        window.localStorage.setItem(COACH_STORAGE_KEY, '1');
    } catch (e) {
        // Nothing to do: the overlay has been shown, and a browser that cannot
        // remember it will show it again next time. Preferable to failing open.
    }
}
function prefersReducedMotion() {
    try {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
        return false;
    }
}

/*
 * SMASH-1978: the AT-facing counterpart of the intro card above.
 *
 * The card is `aria-hidden="true"`, and the note at its JSX justifies that by
 * claiming every capability it advertises "already carries its own accessible
 * name (the close button, the sound toggle, the chevrons)". That holds for
 * three of the pointer card's four rows and is FALSE for the fourth: "Click to
 * Pause" has no control at all on this viewer. Task A removed the persistent
 * play/pause button, so play/pause is reachable only by tapping the stage
 * (not focusable, no name) or by pressing Space (bound in `onKeyDown`,
 * announced nowhere). The offscreen live region added for §6.4 reports the
 * resulting STATE — "Playing" / "Paused" — but names no ACTION, so a
 * screen-reader user is told what happened and never how to cause it.
 *
 * `aria-keyshortcuts` is the honest, cheap close for that gap: it is purely
 * advisory metadata. It binds nothing, changes no behaviour, and adds no Tab
 * stop — it is announced when focus lands on the element carrying it, which
 * here is the element the viewer explicitly focuses on open.
 *
 * SCOPE — one token per DISTINCT ACTION, mirroring the pointer card's four
 * rows in keys rather than gestures: Browse (ArrowUp/ArrowDown), Pause
 * (Space), Close (Escape), Mute (M). Deliberately NOT every bound key.
 * PageUp/PageDown stay bound in `onKeyDown` as navigation aliases and are left
 * unadvertised, because a second token for an action already named buys
 * nothing and this string is read aloud in full — an over-long value buries
 * Space, the one token that is the actual gap. Escape is included even though
 * Radix owns it rather than `onKeyDown`: it is a real working shortcut on this
 * dialog and the sighted card promises it ("Esc to Close"), so dropping it
 * would leave AT users with strictly less than sighted ones, which is the very
 * asymmetry this attribute exists to remove.
 *
 * TOKEN SPELLING is ARIA's, not the DOM's, and they diverge on exactly two of
 * these. The space bar is `Space` because a literal " " is the attribute's own
 * delimiter (WAI-ARIA 1.2 §6.6.4) while `onKeyDown` matches `e.key === ' '`;
 * and `M` is upper-cased as the single canonical token for a binding the
 * handler accepts in either case. Both translations are exercised by the
 * proofs in SwipeView.chrome.test.js, which press the DOM key each token
 * designates rather than trusting the string.
 */
/*
 * `aria-keyshortcuts` — one token per distinct ACTION, not per bound key.
 *
 * Rule 1.17 adds `j`/`k` and `PageUp`/`PageDown` as navigation aliases, and
 * neither appears here. That is deliberate rather than an oversight: this string
 * is read aloud IN FULL when focus lands on the dialog, and a second token for
 * an action already named buys nothing while burying `Space` — the one token
 * that closes a real gap, since play/pause has no on-screen control at all on
 * this viewer.
 *
 * TOKEN SPELLING is ARIA's, not the DOM's, and they diverge on two of these. The
 * space bar is `Space` because a literal " " is the attribute's own delimiter
 * (WAI-ARIA 1.2 §6.6.4) while `onKeyDown` matches `e.key === ' '`; and `M` is
 * upper-cased as the single canonical token for a binding the handler accepts in
 * either case. Both translations are exercised by proofs that press the DOM key
 * each token designates rather than trusting the string.
 */
const KEY_SHORTCUTS = 'ArrowUp ArrowDown Space Escape M';

/*
 * §6.1 interaction counts (SMASH-1853).
 *
 * "No data" and "zero" are DIFFERENT and stay that way: a post with a genuine
 * 0 likes renders "0", while a missing field is omitted entirely. Conflating
 * them would state a number the API never gave us. countOf() is the single
 * place that distinction is made.
 */
function countOf(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

/*
 * Platform-convention abbreviation for the visible label; the accessible name
 * uses the exact figure instead (a screen reader gains nothing from "1.2K").
 *
 * The tier boundary is DERIVED rather than hand-written, because writing it by
 * hand got it wrong twice (Rex review, 2026-08-24: first 1,000,000, then
 * 999,950; the true cut is 999,500). The rule: rounding happens before the
 * suffix is chosen, so a value belongs in a tier as soon as the tier BELOW it
 * would round to 1000 — i.e. at unit/1000 * 999.5, which is exact in binary
 * floating point for every unit here. Deriving it also gave the B tier for
 * free; without it a billion-view video printed "2340M".
 */
// MUST stay in strictly DESCENDING unit order — the loop below takes the first
// tier a value qualifies for. Mis-ordering the existing rows fails loudly (the
// pinned vectors break), but APPENDING a new larger unit at the end fails
// SILENTLY: the row is simply never reached (Rex review, 2026-08-24). The
// ordering is asserted in SwipeView.chrome.test.js, so a new row in the wrong
// place fails there rather than shipping dead.
const COUNT_TIERS = [
    { suffix: 'B', unit: 1000000000 },
    { suffix: 'M', unit: 1000000 },
    { suffix: 'K', unit: 1000 },
];

function abbreviateCount(n) {
    for (let i = 0; i < COUNT_TIERS.length; i++) {
        const { suffix, unit } = COUNT_TIERS[i];
        if (n >= (unit / 1000) * 999.5) {
            const v = n / unit;
            return `${v >= 100 ? Math.round(v) : Math.round(v * 10) / 10}${suffix}`;
        }
    }
    return String(n);
}

/*
 * The spoken form of one metric, e.g. "12,431 likes". Uses `_n()` rather than a
 * ternary over two `__()` strings so locales with more than two plural forms are
 * handled by the translation layer, and translators control where the number
 * sits relative to the noun.
 *
 * All three branches are reachable again (round 3 restored comments and shares),
 * which is what they were written for. They were briefly collapsed to likes
 * alone while the other two metrics were out, because an unreachable branch in a
 * translated string keeps a `.pot` entry alive that no surface can show — a
 * translator then spends effort on copy nobody will read.
 */
function spokenCount(kind, n) {
    const exact = exactCount(n);
    if (kind === 'likes') return sprintf(_n('%s like', '%s likes', n, 'sb-customizer'), exact);
    if (kind === 'comments') return sprintf(_n('%s comment', '%s comments', n, 'sb-customizer'), exact);
    return sprintf(_n('%s share', '%s shares', n, 'sb-customizer'), exact);
}

function exactCount(n) {
    try {
        return n.toLocaleString();
    } catch (e) {
        return String(n);
    }
}

/**
 * Resolves the engagement trio for a post, honouring the feed's existing tile
 * toggles (see useSwipeViewer's `counts`). Each metric is returned separately
 * and is `null` when the API gave us nothing — the rail then omits that item
 * rather than printing a zero the platform never reported.
 *
 * RETURNS THREE FIELDS, not an array, since the 2026-09-08 restyle. The array
 * shape existed to be `.map()`ed into one uniform cluster; TT-3 gives each
 * metric a different job (likes is a readout, comments is a link, share is the
 * one real action), so there is nothing uniform left to map over and a caller
 * that reached for `engagement[1]` would be guessing.
 *
 * ── WHICH METRICS APPEAR IS DECIDED BY PARITY, NOT BY THE DESIGN ────────────
 *
 * Asmita's rule (2026-09-08): "do not miss interaction counters displayed on
 * other layouts on the Swipe layout". So the viewer shows exactly the set the
 * plugin already displays elsewhere, from the same fields, on the same toggles.
 *
 * What the plugin actually displays, audited rather than assumed:
 *
 *   metric   | tiles                       | lightbox              | field
 *   ---------|-----------------------------|-----------------------|--------------
 *   views    | YES, `postElements` 'views' | YES, footer stats row | view_count
 *   likes    | YES, `postElements` 'likes' | YES, footer stats row | like_count
 *   comments | no                          | no                    | comment_count
 *   shares   | no                          | no                    | share_count
 *
 * Tiles: `PostDetails.js` / `PostImage.js` render `Views.js` and `Likes.js`,
 * each gated on `isPro` AND its own `postElements` entry. Lightbox:
 * `Modal.jsx`'s `.sbt-lightbox-modal-stats` — views and likes, nothing else.
 *
 * ── THE DECISION, AS ASMITA SETTLED IT (2026-09-08, round 3) ────────────────
 *
 * ALL FOUR are shown, "if the data is available" — and it is. `comment_count`
 * and `share_count` are cached on every post exactly like the other two, which
 * the audit above records as the reason they were never a data problem in the
 * first place.
 *
 * That resolves the one real tension the audit surfaced. The parity rule ("do
 * not miss counters displayed on other layouts") is a FLOOR — it says which
 * metrics the viewer must not omit, not which it may not add. Reading it as a
 * ceiling is what briefly removed comments and shares, and it cost more than it
 * bought: it took the rail from the design's three metrics to one, and it took
 * the viewer's only genuine in-page ACTION (Web Share) with it, since the share
 * button carried both the count and the action.
 *
 * So: views and likes because every surface shows them; comments and shares
 * because the design draws them and the data supports them. The rail is back to
 * Aman's `avatar -> like -> comment -> share` (+ "View" on touch), and the
 * divergence recorded for him in round 2 is withdrawn.
 *
 * TOGGLES, unchanged and worth restating because it is the part that is easy to
 * get wrong: 'likes' governs the engagement TRIO (likes, comments, shares) and
 * 'views' governs views alone. TikTok's tile UI only ever exposed likes and
 * views, so comments and shares have no toggle of their own to inherit and
 * inventing two would contradict the "extend, don't multiply" rule (§10.1). A
 * feed showing likes but not views still behaves that way inside the viewer.
 */
function describeCounts(post, counts) {
    const engagement = !!(post && counts && counts.engagement);
    return {
        likes: engagement ? countOf(post.like_count) : null,
        comments: engagement ? countOf(post.comment_count) : null,
        shares: engagement ? countOf(post.share_count) : null,
        views: post && counts && counts.views ? countOf(post.view_count) : null,
    };
}

// Resolves the post's caption + attribution as PLAIN TEXT (spec §6.1/§12 —
// captions/titles are attacker-influenceable platform data and must never
// reach the DOM as markup).
function describePost(post, headerData) {
    if (!post) return { title: '', caption: '', author: null };
    let author = headerData;
    if (Array.isArray(author)) {
        const matched = author.length > 1 ? author.filter((h) => h.open_id === post.open_id) : author;
        author = matched[0] || matched;
    }
    const username = author && author.username;
    return {
        title: username ? `@${username}` : '',
        caption: post.video_description || '',
        // Returned so the rail's avatar reads the SAME resolved author object
        // this @handle came from (maintainer review 2026-08-24, item 5). On a
        // multi-source feed the array branch above picks the creator by
        // `open_id`; resolving that a second time somewhere else invites the
        // failure where creator A's avatar sits above creator B's handle.
        // Normalised to null rather than the `[]` an empty filter leaves
        // behind, so a caller can just test truthiness.
        author: author && !Array.isArray(author) ? author : null,
    };
}

/**
 * Creator avatar + profile link for the rail's top disc (maintainer review
 * 2026-08-24, item 5). Returns null whenever there is nothing honest to show —
 * which is what makes the disc OMIT rather than placeholder.
 *
 * GDPR mirrors the read Modal.jsx and Header.js already use against this same
 * `headerData`: with consent the remote CDN copy is allowed; without it, only
 * the locally-cached copy (or the site's own configured avatar placeholder) may
 * be used, because fetching `avatar_url_100` is a third-party request to
 * TikTok. Preferring `local_avatar_url` in BOTH branches is deliberate and
 * matches those call sites — the local copy is always the better choice, and in
 * the no-consent branch it is the only one.
 *
 * That branch is not dead code, even though `useSwipeViewer` refuses to open
 * the viewer at all without consent (spec §2.4): the layouts mount SwipeView on
 * `isSwipeOpen` alone, never re-checking `swipeEnabled`, so consent WITHDRAWN
 * while the viewer is open flips `canLoadExternal` to false under a live
 * viewer. `useGdpr` is read in-component (not snapshotted) for that reason.
 */
function describeCreator(author, canLoadExternal, avatarPlaceholder) {
    if (!author) return null;
    const src = canLoadExternal
        ? (author.local_avatar_url || author.avatar_url_100)
        : (author.local_avatar_url || avatarPlaceholder);
    // `profile_deep_link` is the account's own canonical link — the same field
    // Header.js's profile button uses — with the @handle URL as the fallback
    // shape. BOTH go through safeUrl (§12): a field is not trusted just because
    // it arrived inside headerData rather than a post.
    const href = safeUrl(author.profile_deep_link)
        || (author.username ? safeUrl(`https://www.tiktok.com/@${author.username}`) : '');
    // No image, or nowhere to send the visitor, means no disc. A 44px disc in a
    // rail of working controls that does nothing is worse than its absence, and
    // the @handle is already on screen in the meta block either way.
    if (!src || !href) return null;
    return { src, href, username: author.username || '' };
}

/**
 * Swipe View — TikTok viewer shell (SMASH-1853).
 *
 * Implements the experience spec (docs/technical-designs/swipe-view-
 * experience-spec.md) as a React port of the reference implementation at
 * local-notes/swipe-view-launch/phase0/swipe-prototypes/core/swipe-core.js:
 * transform-based vertical track, wheel/touch/keyboard nav with the spec's
 * thresholds and cooldowns, viewer-owned chrome, the a11y layer, and the
 * fallback chain (delegated per-slot to SwipeSlot.jsx / swipeAdapters.js).
 *
 * `posts` is the eligible-post SNAPSHOT (spec §2.3) — the caller
 * (useSwipeViewer) captures it once at open time and this component MUST
 * NOT recompute it while open.
 */
const SwipeView = ({ posts, startIndex, headerData, counts, opener, onClose }) => {
    // Read live, not snapshotted: consent can be withdrawn while the viewer is
    // open (see describeCreator's docblock). GdprContext ships safe defaults
    // (`gdprActive: false, gdprConsented: true`), so this is also correct with
    // no provider mounted above it.
    const { gdprActive, gdprConsented, gdprAvatarPlaceholder } = useGdpr();
    const canLoadExternal = !gdprActive || gdprConsented;
    const chainRef = useRef(null);
    if (!chainRef.current) chainRef.current = createTikTokAdapterChain();
    const chain = chainRef.current;

    const total = posts.length;
    const [current, setCurrent] = useState(() => Math.max(0, Math.min(startIndex || 0, total - 1)));
    // §4.2 (revised, Maintainer review SMASH-1853 — "sound-on-open"): the
    // viewer opens from a click and every swipe is a gesture, so attempting
    // UNMUTED playback by default is permitted in those contexts. Default is
    // now `true` (try unmuted) rather than the spec's baseline muted default;
    // this single boolean still records "the user's last explicit choice in
    // either direction" exactly as unmute-persistence already did — the ONLY
    // path that ever sets it to `false` is the user explicitly pressing mute
    // (toggleMute below), so a rejected autoplay attempt (handled entirely
    // inside the adapter's own fallback, see swipeAdapters.js `attemptPlay`)
    // never has to flip this back down; the next activation simply retries
    // unmuted, which is what "on activate (open AND per-swipe)" requires.
    const [unmuted, setUnmuted] = useState(true);
    const [slotAssignment, setSlotAssignment] = useState(() =>
        syncSlotWindow(new Array(SLOT_COUNT).fill(null), desiredIndices(current, total))
    );
    // `muted` here is the REAL sampled state (swipeAdapters.js `isMuted()`),
    // never the `unmuted` intent above — Rex review (SMASH-1853): rendering
    // the toggle/pill from intent alone let a rejected attemptPlay() retry
    // leave the element muted while the UI kept claiming sound was on. The
    // `false` default matches the common case (attemptPlay sets the real
    // property synchronously at mount, before this state's first poll tick
    // ever runs) — same startup grace window every other field here already
    // has, self-corrects within the same 250ms cadence.
    // `sampled` records whether any of the above has been read off a real
    // adapter yet, and exists because the `muted: false` default is a
    // PLACEHOLDER, not an observation (Task F, team feedback 2026-08-24).
    // Anything treating "muted === false" as evidence that audio is genuinely
    // on has to check this first, or it will read the placeholder as a fact
    // during the startup grace window — see the unmute-success detector below.
    // `tierIndex` is which rung of the fallback chain the active slide is on
    // (0 = the media tier, above that = a fallback), sampled the same way as
    // everything else here so it tracks a mid-view escalation without any
    // separate bookkeeping. -1 means "nothing instantiated yet"; combined with
    // `sampled` it keeps the platform CTA from flashing on during the startup
    // grace window before the real tier is known.
    const [chrome, setChrome] = useState({ caps: { mute: false, playPause: false, progress: false }, playing: false, muted: false, progress: 0, tierIndex: -1, sampled: false });
    const [reducedMotion] = useState(prefersReducedMotion);
    /*
     * ── The pause glyph (rule 1.10) ─────────────────────────────────────────
     *
     * REPLACES a transient tap-flash — a 64px disc that faded over 600ms on
     * every tap, showing whichever glyph the tap was about to produce — with the
     * design's persistent PLAY TRIANGLE, shown for as long as the slide is
     * paused. The two are different affordances, not different timings: a flash
     * tells you a tap registered, and this tells you the state you are in.
     *
     * `flashOn` is NOT simply "is it paused". It stays true for the dissolve's
     * own 100ms after playback resumes, because the design's exit animation has
     * to have something to animate: unmounting on resume would cut the glyph
     * instantly and lose the half of the motion that "gets out of the way".
     */
    const [flashOn, setFlashOn] = useState(false);
    /*
     * ── The progress bar's "jump on nav" fix (rule 1.12) ────────────────────
     *
     * The bar eases its width over 200ms, which is right while a clip plays and
     * wrong at the moment a slide changes: the fill has to travel from wherever
     * the outgoing slide had reached back to zero, so it visibly RUNS BACKWARDS
     * across the frame under the incoming video.
     *
     * The fix is to write the new value SYNCHRONOUSLY with the transition
     * suppressed for that one write. This flag is the suppression, and it is
     * cleared on the next macrotask so the following poll tick eases normally.
     * A `setTimeout(…, 0)` rather than `requestAnimationFrame` because the
     * suites drive this under jsdom, where rAF exists but is not tied to paint.
     */
    const [progressInstant, setProgressInstant] = useState(false);
    /*
     * Resolved ONCE PER OPEN, which here means once per mount: the layouts mount
     * SwipeView on `isSwipeOpen` alone, so a mount IS an open. A lazy
     * initialiser is the right shape for that — the same idiom `reducedMotion`
     * uses — and it means a visitor whose device capabilities change mid-view
     * keeps what they opened with rather than having an overlay appear under
     * them.
     */
    const [coachVisible, setCoachVisible] = useState(() => isTouchLayout() && !coachAlreadySeen());
    /*
     * "The browser REFUSED sound", armed only by a genuinely rejected unmuted
     * `play()` — see the `autoplay_blocked` branch of handleSlotAnalytics below.
     *
     * This used to gate a separate bottom-centre "Tap to unmute" PILL. Rule 1.6
     * folds the affordance into the mute button itself, so the same evidence now
     * decides whether that button hints (rule 1.7's failure arm) rather than
     * whether a second control exists. The arming condition is deliberately
     * unchanged: nothing weaker than a real rejection may show the label, or the
     * viewer would nag a visitor who muted on purpose.
     */
    const [unmuteRefused, setUnmuteRefused] = useState(false);
    /*
     * Whether the mute button is currently sliding its label out (rule 1.7(b)).
     *
     * Separate from `unmuteRefused` because the two answer different questions:
     * that one is "is sound refused right now", this one is "is the label out
     * right now", and the label is a TIMED beat inside a refused state rather
     * than the state itself — 350ms in, retracted at 4500ms, replayed on each
     * new slide while still refused, and gone for good once audio lands.
     */
    const [muteHinting, setMuteHinting] = useState(false);

    const currentRef = useRef(current);
    currentRef.current = current;
    const slotAssignmentRef = useRef(slotAssignment);
    slotAssignmentRef.current = slotAssignment;
    const navLockRef = useRef(false);
    /* Wheel accumulator state (rule 1.16) — none of it is rendered. */
    const wheelAccumRef = useRef(0);
    const wheelLastEventRef = useRef(0);
    const wheelLastNavRef = useRef(0);
    const wheelLockedRef = useRef(false);
    const viewedRef = useRef(new Set([current]));
    const slotRefs = useRef(
        Array.from({ length: SLOT_COUNT }, () => null)
    ).current; // stable array of refs, filled in via the ref callback below.
    // Initial-focus target on open (Maintainer feedback 2026-08-18): focusing the
    // close button painted its :focus-visible ring on every open in engines whose
    // modality heuristic treats Radix's async autofocus as non-pointer (WebKit).
    // APG dialog pattern: container focus is equally valid, shows no ring, and a
    // keyboard user's first Tab still lands on close — where the ring correctly
    // appears. §7.1 restoration (opener-explicit) is unaffected.
    const contentRef = useRef(null);
    const flashTimerRef = useRef(null);
    /*
     * Task F (team feedback 2026-08-24): "auto-hide after the first SUCCESSFUL
     * unmute". Per viewer instance, and set from EVIDENCE only — see the
     * detector effect further down; nothing else may set it.
     *
     * The distinction between "the visitor tapped" and "audio is actually on"
     * is the entire point. Suppressing on a mere tap would mean a later slide
     * that genuinely gets refused audio shows no pill — the visitor gets
     * silence with no affordance and no way to ask again. Suppressing after a
     * REAL unmute is safe for the mirror-image reason: by then the browser has
     * granted this origin audio for the session, so later activations aren't
     * refused and there is nothing left to offer.
     */
    const unmuteSucceededRef = useRef(false);

    // Recompute the 3-slot window whenever the active index changes (spec
    // §4.4). syncSlotWindow keeps any slot already holding a still-desired
    // index in place — that stability is what makes a one-step back-swipe
    // instant and lets iframe adapters recycle instead of rebuilding.
    useEffect(() => {
        setSlotAssignment((prev) => syncSlotWindow(prev, desiredIndices(current, total)));
    }, [current, total]);

    /* ------------------------------------------------------------ analytics */

    const emit = useCallback(
        (eventName, detail, postId) => {
            emitSwipeAnalytics(eventName, detail, postId ?? (posts[currentRef.current] && posts[currentRef.current].id));
        },
        [posts]
    );

    // Bridge from a slot's internal escalation/watchdog bookkeeping to the
    // spec's named analytics events (§8). `trackIndex` here is the slot's
    // OWN assigned index (captured via a ref inside SwipeSlot), not
    // necessarily the currently-active one — a late async event from a
    // just-abandoned slot should still attribute correctly.
    const handleSlotAnalytics = useCallback(
        (kind, detail, trackIndex, postId) => {
            const index = trackIndex ?? currentRef.current;
            if (kind === 'playback_fallback') emit('swipeview_playback_fallback', { index, ...detail }, postId);
            else if (kind === 'playback_stalled') emit('swipeview_playback_stalled', { index, ...detail }, postId);
            else if (kind === 'terminal_tier') emit('swipeview_terminal_tier', { index, ...detail }, postId);
            else if (kind === 'autoplay_blocked') {
                emit('swipeview_autoplay_blocked', { index, ...detail }, postId);
                // Task B: only surface the pill for the slide the visitor is
                // actually looking at — a neighbour slot can independently
                // hit this same rejection (e.g. while recycling ahead of a
                // swipe) and must not pop a pill for an off-screen slide.
                //
                // Task F: …and never again once a real unmute has landed in
                // this viewer session. Note the appear-condition itself is
                // UNCHANGED — still only a genuinely rejected unmuted play.
                if (
                    !unmuteSucceededRef.current &&
                    index === currentRef.current &&
                    detail &&
                    detail.intended === 'unmuted'
                ) {
                    setUnmuteRefused(true);
                }
            }
        },
        [emit]
    );

    /* --------------------------------------------------------- open / close */

    useEffect(() => {
        document.documentElement.classList.add('sbtt-swipeview-locked'); // §7.1 scroll lock.
        emit('swipeview_opened', { index: current, total });
        return () => {
            document.documentElement.classList.remove('sbtt-swipeview-locked');
        };
        // Intentionally run once: this is mount/unmount bookkeeping, not a
        // reaction to `current`/`total` changing while open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const close = useCallback(() => {
        emit('swipeview_closed', { index: currentRef.current, slides_viewed: viewedRef.current.size });
        onClose();
    }, [emit, onClose]);

    // Cleans up the tap-flash timer on unmount — belt-and-suspenders since
    // the state it clears can't cause a leak by itself, but a stray timer
    // firing setState after unmount is exactly the class of warning this
    // avoids for free.
    useEffect(() => {
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        };
    }, []);

    /* -------------------------------------------------------------- chrome */

    // One sampling pass over the ACTIVE slot's imperative handle (mirrors the
    // reference's `_tick`) rather than threading state up through every slot on
    // every frame. Extracted from the interval below so the sound controls can
    // trigger the SAME read on demand — two code paths sampling player state
    // two different ways is how the label/reality split (Rex finding #1) got
    // in last time.
    const sampleChrome = useCallback(() => {
        const activeSlotPos = slotAssignmentRef.current.indexOf(currentRef.current);
        const handle = activeSlotPos !== -1 ? slotRefs[activeSlotPos] : null;
        if (!handle) return;
        const caps = handle.getCaps();
        const playing = handle.isPlaying();
        const muted = handle.isMuted();
        const progressInfo = handle.getProgress();
        const pct = progressInfo && progressInfo.duration > 0 ? (progressInfo.current / progressInfo.duration) * 100 : 0;
        const tierIndex = handle.getTierIndex();
        setChrome({ caps, playing, muted, progress: pct, tierIndex, sampled: true });
    }, [slotRefs]);

    // Clears the progress bar's one-render transition suppression (rule 1.12).
    // Runs after the render that zeroed the fill has committed, so the next
    // sampled value eases as usual.
    useEffect(() => {
        if (!progressInstant) return undefined;
        const id = setTimeout(() => setProgressInstant(false), 0);
        return () => clearTimeout(id);
    }, [progressInstant]);

    // §6.1: play/pause + progress "refreshed at least every 250ms".
    useEffect(() => {
        const id = setInterval(sampleChrome, 250);
        return () => clearInterval(id);
    }, [sampleChrome]);

    /*
     * A user-initiated mute change is the one moment the 250ms cadence is
     * actually FELT — the visitor taps and waits for the sound. This re-reads
     * real player state on the next macrotask instead of leaving them to the
     * next tick.
     *
     * `setTimeout(…, 0)` rather than a synchronous call is load-bearing, not
     * defensive padding. swipeAdapters' `setMuted(false)` routes through
     * `attemptPlay`, which assigns `video.muted = false` BEFORE calling play(),
     * so a synchronous read reports success even when the play() promise is
     * about to reject and mute the element straight back. That rejection
     * handler is a microtask, and microtasks all drain before the next
     * macrotask — so by the time this callback runs, the real outcome (audio
     * on, or fallen back to muted) has already landed on the element. Sampling
     * any earlier would manufacture exactly the false "sound is on" this
     * viewer's review history is full of.
     */
    const resampleChrome = useCallback(() => {
        setTimeout(sampleChrome, 0);
    }, [sampleChrome]);

    /*
     * Task F: the ONLY place `unmuteSucceededRef` is ever set. "Success" means
     * the label was armed by a genuine rejection AND real sampled state now
     * reports audio on — nothing weaker.
     *
     * Two guards, and BOTH are load-bearing — each one is a different way for
     * `muted === false` to mean something other than "audio is on":
     *
     *   - `chrome.sampled`: `muted: false` starts as a placeholder (see the
     *     chrome state above), and the rejection that arms the pill fires
     *     during slide activation — well before the first poll tick. Without
     *     this guard the placeholder reads as a successful unmute the instant
     *     the pill arms, suppressing it permanently on the very first
     *     rejection: the exact failure this item exists to prevent, one line
     *     deeper.
     *   - `chrome.caps.mute`: on the embed and poster tiers `isMuted()` is
     *     documented in swipeAdapters.js as ADVISORY ONLY — the embed returns
     *     a flat `false` because that matches the platform's default, not
     *     because anything was observed. A slide escalating off the native
     *     tier while the pill is up therefore reports `muted: false` with no
     *     unmute having happened, and without this guard that would latch the
     *     suppression for the whole session, silently denying the pill to a
     *     LATER native-tier slide that genuinely needs it. Only a tier that
     *     actually owns a mute control can testify about one.
     */
    useEffect(() => {
        if (!unmuteRefused) return;
        if (!chrome.sampled || !chrome.caps.mute || chrome.muted) return;
        unmuteSucceededRef.current = true;
        setUnmuteRefused(false);
    }, [unmuteRefused, chrome.sampled, chrome.caps.mute, chrome.muted]);

    /*
     * ── The label's beat (rule 1.7(b), spec §2.4's measured timing) ─────────
     *
     * 350ms in, out at 4500ms, re-armed on every slide change while the viewer
     * is still muted-because-refused. `current` is in the dependency list for
     * exactly that: the design replays this per slide ("it keeps animating if
     * you are muted"), and a slide change is the event that replays it.
     *
     * The three-way gate is the same one the pill's render used, and each part
     * still earns its place. `unmuteRefused` is the evidence a rejection really
     * happened; `chrome.muted` is live reality, so the label retracts the moment
     * audio is genuinely on regardless of which handler got it there; and
     * `chrome.caps.mute` stops the label outliving a tier that has no sound
     * control at all (§6.2) — a slide can escalate from native <video> to an
     * embed while the label is out, and `setMuted` is a no-op there.
     *
     * Rule 1.7(c) needs no code here: `unmuteRefused` is only ever armed by a
     * rejected attempt, and an explicit mute goes through `toggleMute`, which
     * never arms it. So a visitor who muted on purpose is never told to unmute.
     */
    useEffect(() => {
        if (!unmuteRefused || !chrome.muted || !chrome.caps.mute) {
            setMuteHinting(false);
            return undefined;
        }
        const inId = setTimeout(() => setMuteHinting(true), MUTE_HINT_IN_MS);
        const outId = setTimeout(() => setMuteHinting(false), MUTE_HINT_OUT_MS);
        return () => {
            clearTimeout(inId);
            clearTimeout(outId);
        };
    }, [unmuteRefused, chrome.muted, chrome.caps.mute, current]);

    /*
     * ── The pause glyph's lifecycle (rule 1.10) ─────────────────────────────
     *
     * `paused` is derived from REAL sampled state, and the `chrome.sampled`
     * guard is what keeps the glyph off the screen during the startup grace
     * window: `playing` starts as a placeholder `false`, so without it every
     * slide would flash a play triangle for the first poll tick before the
     * adapter has said anything.
     *
     * §6.2: suppressed entirely on a tier with no play/pause capability, rather
     * than painting a control that tier cannot honour — the same rule the mute
     * button follows by disabling instead of hiding.
     *
     * The exit is the interesting half. On resume the element stays mounted for
     * the dissolve's own duration and then unmounts, because the design's
     * animation is ASYMMETRIC on purpose — appearing is a statement (200ms
     * bloom from `scale(.3)` with a blur), disappearing gets out of the way
     * (100ms to `scale(.8)`). Unmounting on resume would cut that second half
     * off entirely.
     */
    const paused = chrome.sampled && chrome.caps.playPause && !chrome.playing;
    useEffect(() => {
        if (paused) {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
            setFlashOn(true);
            return undefined;
        }
        if (!flashOn) return undefined;
        // Matches the dissolve's duration in SwipeView.css. The two have to
        // agree: unmount early and the animation is clipped mid-flight; unmount
        // late and an invisible box lingers over the gesture layer.
        flashTimerRef.current = setTimeout(() => setFlashOn(false), FLASH_OUT_MS);
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        };
    }, [paused, flashOn]);

    /*
     * The coach overlay's whole lifecycle, in one effect.
     *
     * It runs only when the overlay is actually up, so a desktop visitor (or a
     * returning one) binds nothing at all — the `coachVisible` dependency is
     * what makes this free rather than a set of listeners filtering themselves.
     *
     * The flag is written the moment it is SHOWN, not when it is dismissed, and
     * that is deliberate: a visitor who closes the viewer during the first
     * 3200ms has still been shown it, and re-showing on their next open would
     * make "once ever" mean "once per open that lasted long enough".
     *
     * `{ passive: true }` on the dismiss listeners, unlike the gesture layer's
     * — these only observe, never `preventDefault`, and a passive listener
     * cannot block scrolling while the browser decides what the touch meant.
     */
    useEffect(() => {
        if (!coachVisible) return undefined;
        markCoachSeen();

        let armTimer = null;
        let autoTimer = null;
        const dismiss = () => setCoachVisible(false);
        const listeners = ['touchstart', 'wheel', 'scroll'];
        const detach = () => listeners.forEach((type) => window.removeEventListener(type, dismiss));

        armTimer = setTimeout(() => {
            listeners.forEach((type) => window.addEventListener(type, dismiss, { passive: true }));
        }, COACH_ARM_MS);
        autoTimer = setTimeout(dismiss, COACH_AUTO_DISMISS_MS);

        return () => {
            clearTimeout(armTimer);
            clearTimeout(autoTimer);
            detach();
        };
    }, [coachVisible]);

    const activeSlotHandle = useCallback(() => {
        const activeSlotPos = slotAssignment.indexOf(current);
        return activeSlotPos !== -1 ? slotRefs[activeSlotPos] : null;
    }, [slotAssignment, current, slotRefs]);

    /* ------------------------------------------------------------- nav/gestures */

    /*
     * Returns whether it actually navigated (SMASH-1978). Every caller but the
     * drag-commit ignores that, and the drag-commit needs it for one reason:
     * the per-reel chrome's return to rest animates on a RETURN and must not on
     * a COMMIT (see `chromeTravelStyle`), and "did `go` refuse?" is the only
     * thing that distinguishes the two at the moment the gesture ends. Reading
     * `current` back would not work — the state update has not landed yet.
     */
    /*
     * ── Priming: bank one autoplay grant per pooled element ─────────────────
     *
     * iOS unmute persistence (SMASH-1853 follow-up). A muted `play()` followed
     * immediately by `pause()` is enough for WebKit to bank an element's
     * audible-playback grant, and the grant then survives programmatic plays.
     * §4.4's pre-warm rule is honoured as "never audible, never left playing"
     * rather than "never played" — the adapter's `prime()` enforces both halves
     * rather than promising them.
     *
     * Once per SLOT, not once per session, and the distinction is load-bearing.
     * A single session-wide flag looked equivalent and was not: the mount call
     * below runs before the neighbours have elements at all, so it banked
     * nothing and then consumed the flag, leaving the pool unprimed for the
     * whole session. Measured — the priming case in SwipeView.prewarm.test.js
     * saw zero muted plays on the first navigation until this became per-slot.
     *
     * Per-slot is also the honest unit. The pool's three elements are reused
     * across posts now (see SwipeSlot's recycle gate), so a slot position and
     * an element are the same thing for the life of the viewer — "this slot is
     * banked" really does mean "this element is banked", and a slot that had no
     * element yet simply gets primed on the next gesture instead of being
     * written off.
     *
     * Called from `go()` (any gesture-driven navigation) and, best-effort, from
     * a mount effect. The mount call is the one that could pay off on the
     * OPENING tap, but a passive effect is not guaranteed to still be inside
     * that tap's task, so it is a bonus and the first gesture is the path
     * relied on.
     */
    const bankedSlotsRef = useRef(new Set());
    const bankGrants = useCallback((skipSlotPos) => {
        let banked = 0;
        slotRefs.forEach((handle, pos) => {
            if (bankedSlotsRef.current.has(pos)) return;
            /*
             * The slot being activated for real is skipped by POSITION rather
             * than by asking the element whether it is paused. The adapter does
             * check that too, but only the caller knows it just issued an
             * unmuted play for this slot, and a muted prime landing after it
             * would silence the slide the visitor is watching. Position is
             * knowledge; `paused` is a race.
             *
             * It is marked banked all the same: `playNow()` just gave it a
             * gesture-backed play, which IS the bank — there is nothing left
             * for a prime to add.
             */
            if (pos === skipSlotPos) {
                bankedSlotsRef.current.add(pos);
                return;
            }
            if (handle && handle.prime && handle.prime()) {
                bankedSlotsRef.current.add(pos);
                banked += 1;
            }
        });
        return banked > 0;
    }, [slotRefs]);

    useEffect(() => {
        bankGrants();
        // Mount only — see `bankGrants`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const go = useCallback(
        (delta) => {
            const target = currentRef.current + delta;
            if (navLockRef.current || target < 0 || target >= total) return false;
            navLockRef.current = true;
            setTimeout(() => {
                navLockRef.current = false;
            }, NAV_COOLDOWN_MS);
            viewedRef.current.add(target);

            /*
             * ── The activation play happens HERE, in the gesture ────────────
             *
             * iOS unmute persistence (SMASH-1853 follow-up). On WebKit the
             * permission to play AUDIBLY is granted per media element and
             * banked by a gesture-backed `play()`. `go()` used to set state and
             * nothing else, leaving the actual play to the slot's activation
             * effect — a passive effect that runs two renders later, outside
             * the gesture's task. Every swipe therefore asked for sound with no
             * activation in scope, WebKit refused, and the viewer fell back to
             * muted. The chevron `click` path was the starkest case: a perfect
             * user activation, discarded.
             *
             * `go()` is called synchronously from every navigation path — the
             * touchend commit, the wheel accumulator, the keydown, and both
             * chevron clicks — so doing it here fixes all five at once rather
             * than five call sites drifting apart.
             *
             * Both halves are synchronous and both matter. Playing the incoming
             * slot without pausing the outgoing one leaves two slides audible
             * until the effects reconcile; pausing without playing is today's
             * bug with extra steps.
             *
             * The slot window already holds the target — that is what the
             * three-slot pool is FOR — so this reaches a real element rather
             * than racing the window sync. If it does not (a jump wider than
             * the window, a slot owing a fresh chain walk), `playNow()` returns
             * false and the activation effect remains the authority, exactly as
             * before.
             */
            const outgoingPos = slotAssignmentRef.current.indexOf(currentRef.current);
            const incomingPos = slotAssignmentRef.current.indexOf(target);
            const outgoing = outgoingPos !== -1 ? slotRefs[outgoingPos] : null;
            const incoming = incomingPos !== -1 ? slotRefs[incomingPos] : null;
            if (outgoing && outgoing.pauseNow) outgoing.pauseNow();
            if (incoming && incoming.playNow) incoming.playNow();
            /*
             * And bank the REST of the pool while a gesture is still in scope.
             * The imperative play above covers the slide being navigated to;
             * priming covers the ones after it, because the pool's three
             * elements are now reused across posts (see SwipeSlot's recycle
             * gate) and a grant banked here lasts as long as the element does.
             */
            bankGrants(incomingPos);

            setCurrent(target);
            /*
             * Rule 1.12: zero the fill in the SAME render that changes the
             * index, with the transition off for that write. Both halves matter
             * — writing it a tick later still animates the run-back, and writing
             * it without the suppression animates it faster.
             */
            setChrome((c) => (c.progress === 0 ? c : { ...c, progress: 0 }));
            setProgressInstant(true);
            // A new slide gets its own fresh activation cycle — an unmute
            // refusal recorded against the slide being swiped away is stale, and
            // carrying it over would hint at a slide that never asked.
            setUnmuteRefused(false);
            //
            // The PAUSE GLYPH needs no clearing here, and that asymmetry is the
            // point of rule 1.10's model: it is DERIVED from real sampled state
            // rather than set by a handler, so it follows the incoming slide's
            // own playing/paused reading on the next tick. The transient
            // tap-flash it replaced had to be cleared by every path that changed
            // slides — and was, from here.
            emit(delta > 0 ? 'swipeview_next' : 'swipeview_prev', { index: target });
            return true;
        },
        [total, emit, bankGrants, slotRefs]
    );

    /*
     * Play/pause on the SAME tap the gesture layer already handles — this adds
     * no new click target (§3.3 stays satisfied by the existing gesture-layer
     * `onClick` wiring below).
     *
     * `resampleChrome()` is what makes the glyph feel immediate, and it replaces
     * the flash's own timer. The glyph is driven off REAL sampled state rather
     * than from the intent of the tap, which is the right call — a `pause()` a
     * tier refuses must not paint a pause glyph — but the sampling cadence is
     * 250ms, and a quarter-second lag on a tap the visitor just made is felt.
     * Re-sampling on the next macrotask closes that without inventing a second,
     * optimistic source of truth for "is it playing".
     */
    const togglePlay = useCallback(() => {
        const handle = activeSlotHandle();
        if (!handle) return;
        handle.togglePlay();
        resampleChrome();
    }, [activeSlotHandle, resampleChrome]);

    // Rex review (SMASH-1853): this must flip based on the REAL sampled
    // state (`chrome.muted`), not the `unmuted` intent. After a rejected
    // attemptPlay() retry falls back to muted, the intent never flips (by
    // design — §4.2's fallback is per-attempt, not a persisted choice), so
    // the toggle's own label already correctly says "Unmute" at that point
    // (driven by chrome.muted below). If this handler kept toggling off the
    // STALE intent instead, pressing that "Unmute" button would silently
    // mute it further — acting on the same real state the label reads from
    // is what keeps the label and the action in agreement.
    const toggleMute = useCallback(() => {
        const shouldUnmute = chrome.muted;
        const handle = activeSlotHandle();
        if (handle) handle.setMuted(!shouldUnmute);
        setUnmuted(shouldUnmute); // record the new explicit choice for future activations (spec §4.2).
        // Task F: no longer clears the pill by hand. Whenever the pill is up
        // the real state is muted, so this control reads "Unmute" and pressing
        // it is always an unmute ATTEMPT — and an attempt is not proof. The
        // pill's own render gate drops it once audio is genuinely on; the
        // re-sample below brings that moment forward to the next macrotask.
        resampleChrome();
        emit(shouldUnmute ? 'swipeview_unmuted' : 'swipeview_muted', { index: currentRef.current });
    }, [chrome.muted, activeSlotHandle, emit, resampleChrome]);

    // Task B: the pill's own tap. This — unlike the per-swipe/watchdog/loop
    // reactivations §4.2's fallback exists for — genuinely IS a user
    // gesture, so calling the adapter's setMuted(false) synchronously from
    // inside this click handler gives the browser a real activation to
    // authorise the retry against (see swipeAdapters.js `attemptPlay`,
    // reached via SwipeSlot's `setMuted` imperative method). `unmuted` is
    // very likely already `true` here (it's the default) — set it anyway
    // rather than reading it as a signal, so the explicit-choice semantics
    // stay correct even if a future caller reaches this from elsewhere.
    //
    const onWheel = useCallback(
        (e) => {
            /*
             * `preventDefault` UNCONDITIONALLY, before any of the accumulation
             * logic and regardless of whether this event navigates. The viewer
             * owns every gesture (§3.3), and a wheel event allowed through
             * scrolls the host page behind the overlay on any host where the
             * `<html>` scroll-lock class is defeated. This is also the assertion
             * the chrome suite uses to prove the listener is bound natively:
             * under React's passive root listener `preventDefault` is a silent
             * no-op, so a cancelled event is only possible via `bindGestures`.
             */
            e.preventDefault();
            // A pinch-zoom arrives as a wheel event with `ctrlKey` set. It is
            // not a scroll and must not navigate.
            if (e.ctrlKey) return;

            const now = gestureNow();
            const gap = now - wheelLastEventRef.current;
            wheelLastEventRef.current = now;

            /*
             * A quiet gap longer than 160ms means the previous gesture ENDED, so
             * the accumulator resets and the lock lifts. This one branch is what
             * separates a deliberate new scroll from the tail of the last one —
             * an inertia tail arrives as a dense stream with no such gap.
             */
            if (gap > WHEEL_GESTURE_END_MS) {
                wheelAccumRef.current = 0;
                wheelLockedRef.current = false;
            }

            // Still inside the gesture that already navigated: this is the tail.
            if (wheelLockedRef.current) return;

            wheelAccumRef.current += e.deltaY;
            if (Math.abs(wheelAccumRef.current) < WHEEL_ACCUM_PX) return;

            /*
             * The 450ms floor. Checked AFTER the accumulator so a slow scroll
             * arriving during the interval keeps building rather than being
             * discarded — it then fires as soon as the floor clears, which is
             * what makes a continuous drag feel continuous.
             */
            if (now - wheelLastNavRef.current < WHEEL_MIN_INTERVAL_MS) return;

            const direction = wheelAccumRef.current > 0 ? 1 : -1;
            wheelAccumRef.current = 0;
            wheelLockedRef.current = true;
            wheelLastNavRef.current = now;
            go(direction);
        },
        [go]
    );

    // React attaches its root-level wheel, touchstart and touchmove listeners
    // PASSIVELY, so an `onWheel` / `onTouchMove` PROP can never preventDefault —
    // Chrome logs "Unable to preventDefault inside passive event listener
    // invocation" on every scroll (maintainer report, 2026-08-18) and
    // scroll-chaining would leak on any host where the <html> scroll-lock class
    // is defeated. Bind natively with passive: false — the same reason IG's
    // sbi-quickscroll binds wheel directly.
    //
    // Bound via CALLBACK REF, not a [handler]-dep effect: inside Radix's
    // portal/Presence mount dance an effect can run against a null or
    // since-replaced node and (deps unchanged) never rebind — the listener
    // then sits on a detached div and the gesture silently dies. The callback
    // ref fires exactly when the real node attaches/detaches. Listener
    // identities are stable and delegate to the latest handler through a ref,
    // so rebinding is never needed on re-render.
    //
    // SMASH-1978 widened this from wheel alone to the whole touch sequence.
    // `touchmove` is the one that strictly REQUIRES it (it is the handler that
    // calls preventDefault), but binding the whole set through one path keeps
    // the gesture's start, move and end from living under two different event
    // systems with two different passivity rules.
    const gesturesNodeRef = useRef(null);
    const liveGestureHandlersRef = useRef({});
    const gestureListenersRef = useRef(null);
    const bindGestures = useCallback((node) => {
        if (!gestureListenersRef.current) {
            // One stable listener per type, each reading the current handler out
            // of the live ref at call time.
            gestureListenersRef.current = {
                wheel: (e) => liveGestureHandlersRef.current.wheel(e),
                touchstart: (e) => liveGestureHandlersRef.current.touchstart(e),
                touchmove: (e) => liveGestureHandlersRef.current.touchmove(e),
                touchend: (e) => liveGestureHandlersRef.current.touchend(e),
                touchcancel: (e) => liveGestureHandlersRef.current.touchcancel(e),
            };
        }
        const listeners = gestureListenersRef.current;
        const previous = gesturesNodeRef.current;
        if (previous && previous !== node) {
            Object.keys(listeners).forEach((type) => previous.removeEventListener(type, listeners[type]));
        }
        gesturesNodeRef.current = node;
        if (node) {
            Object.keys(listeners).forEach((type) => node.addEventListener(type, listeners[type], { passive: false }));
        }
    }, []);

    /* ------------------------------------------------- thumb-follow drag */

    /*
     * SMASH-1978: the track follows the finger 1:1 while a touch is down, and
     * snaps on release to whichever slide holds most of the viewport.
     *
     * `dragRef` holds the immutable facts of the gesture in progress — where it
     * started, when, how tall the viewport was, and whether it has travelled far
     * enough to stop being a tap. Nothing in here is rendered, so it is a ref;
     * the one value that IS rendered (`dragOffset`) is state.
     *
     * The viewport height is sampled ONCE, at touchstart, rather than read per
     * move: it is the denominator of the area rule and the reach of the clamp, so
     * a mid-gesture change (an address bar collapsing under the finger is the
     * realistic one) would silently move the boundary the visitor is aiming at.
     */
    const dragRef = useRef(null);
    const [dragOffset, setDragOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    /*
     * SMASH-1978, second half: whether the per-reel chrome ANIMATES its return
     * to rest. See `chromeTravelStyle` below for the whole mechanism; this flag
     * exists only to separate the two ways `dragOffset` gets back to 0.
     *
     * A RETURN (the drag didn't earn a commit, or a cancel/pinch aborted it)
     * leaves the same reel on screen, so the chrome animates home exactly as
     * the track does — identical duration, identical easing, because a
     * divergence there is visible as the chrome sliding at a different speed
     * than the video it is captioning.
     *
     * A COMMIT swaps which reel the chrome describes. Animating the stale delta
     * out then moves the INCOMING reel's chrome in the opposite direction to its
     * own reel's travel, which is worse than not animating at all — so on a
     * commit the chrome lands at rest in the same paint that swaps its content.
     * Default `true` so a gesture that never commits (the common hesitant drag)
     * animates; `endDrag` re-arms it whenever a gesture ends, and `onTouchEnd`
     * turns it off again for the one path that commits.
     */
    const [chromeSnapAnimates, setChromeSnapAnimates] = useState(true);

    // A gesture that moved is not a tap. The gesture layer carries a real
    // `onClick` for tap-to-toggle, and a touch sequence can still synthesise one
    // — engines disagree about whether `preventDefault` on touchmove suppresses
    // it — so without this every swipe would also pause the video. Read and
    // cleared by the click handler below, never rendered.
    const suppressTapRef = useRef(false);

    /*
     * Ending a drag ALWAYS captures its tap suppression, which is why the two
     * live in one function rather than at each call site.
     *
     * Three paths end a gesture — a release, a cancel, and a second finger
     * aborting a pinch — and any of them can be followed by a synthesised
     * click. The capture was originally written at the release and cancel call
     * sites and the pinch-abort was missed, so an aborted pinch toggled
     * playback: the same defect this suppression exists to prevent, reached by
     * the one path that did not set the flag. Making the capture part of ENDING
     * a drag is what stops a fourth path from reintroducing it.
     *
     * The flag tracks `moved` rather than "a gesture ended", and that
     * distinction is load-bearing in the other direction: `onGestureClick` is
     * the only thing that ever clears it, so arming it for a gesture that never
     * passed tap slop would leave it set with no click coming — and swallow the
     * visitor's next genuine tap instead. A contact that stayed inside the slop
     * is indistinguishable from a tap, however it ended.
     */
    const endDrag = useCallback(() => {
        const drag = dragRef.current;
        if (drag) suppressTapRef.current = drag.moved;
        dragRef.current = null;
        setDragging(false);
        setDragOffset(0);
        /*
         * Ending a drag also re-arms the per-reel chrome's animated return
         * (SMASH-1978), and it lives here for the same reason the tap-suppression
         * capture above does: three paths end a gesture, and only ONE of them
         * (`onTouchEnd`) has an opinion about whether this should be off.
         *
         * `onTouchEnd` overrides this a moment later when its gesture actually
         * committed — same handler, same React batch, later call wins. The other
         * two paths (a cancel, a second finger aborting a pinch) are returns by
         * definition and are correct with the value set here. Writing this at the
         * call sites instead is what left a commit followed by a CANCEL jumping
         * the chrome home while the track eased: the flag was still off from the
         * commit, and nothing on that path turned it back on.
         */
        setChromeSnapAnimates(true);
    }, []);

    const onTouchStart = useCallback((e) => {
        // Single finger only. A second touch means a pinch or a stray palm, and
        // following the first one through it produces a track that lurches.
        // `endDrag` carries the tap suppression for this path too — an aborted
        // pinch that had already travelled is not a tap.
        if (!e.touches || e.touches.length !== 1) {
            if (dragRef.current) endDrag();
            return;
        }
        const node = gesturesNodeRef.current;
        const height = node ? node.getBoundingClientRect().height : 0;
        dragRef.current = {
            startY: e.touches[0].clientY,
            startTime: gestureNow(),
            height,
            moved: false,
        };
        setDragging(true);
        setDragOffset(0);
    }, [endDrag]);

    const onTouchMove = useCallback((e) => {
        const drag = dragRef.current;
        if (!drag || !e.touches || e.touches.length !== 1) return;
        // Non-negotiable while a drag is live: without it the host page scrolls
        // underneath the viewer on any host where the <html> scroll-lock class is
        // defeated, and pull-to-refresh can fire on a downward drag from the
        // first slide. This is why the listener is bound natively — see
        // `bindGestures`; React's own touchmove listener is passive, where
        // preventDefault is a silent no-op.
        e.preventDefault();
        const raw = e.touches[0].clientY - drag.startY;
        if (!drag.moved && !isTap(raw)) {
            drag.moved = true;
        }
        setDragOffset(clampDragOffset(raw, {
            index: currentRef.current,
            total,
            viewportHeight: drag.height,
        }));
    }, [total]);

    const onTouchEnd = useCallback(
        (e) => {
            const drag = dragRef.current;
            if (!drag) return;
            // Read the final position from the touch that left rather than from
            // state: the last touchmove and this touchend can arrive in the same
            // frame, and state would be one move behind.
            const last = e.changedTouches && e.changedTouches[0];
            const raw = last ? last.clientY - drag.startY : 0;
            const offset = clampDragOffset(raw, {
                index: currentRef.current,
                total,
                viewportHeight: drag.height,
            });
            const direction = decideSnap({
                dragOffset: offset,
                viewportHeight: drag.height,
                durationMs: gestureNow() - drag.startTime,
            });
            // Clearing the offset here — rather than leaving it to the effect
            // below — is what makes the snap animate instead of jump: this
            // render carries both the new index and a zero offset, so the track
            // travels the remaining distance under the normal transition. It
            // also captures the tap suppression, off the same `dragRef` this
            // handler has been reading.
            endDrag();
            // Routed through `go` on purpose, and this is the whole reason the
            // commit is one line: a drag-commit IS a navigation, so it inherits
            // the cooldown gate, the caption collapse, the stale-pill and
            // tap-flash clears, the viewed-set bookkeeping and the prev/next
            // analytics from the same place wheel and keyboard nav get them. If
            // `go` refuses (navLock held), nothing is queued and the track has
            // already been told to return — matching the wheel path, which also
            // simply drops a gesture that arrives during cooldown.
            //
            // A refused `go` is a RETURN, not a commit, which is why this reads
            // `go`'s answer rather than `direction` alone: during the nav
            // cooldown the track visibly returns, and the chrome has to return
            // with it (SMASH-1978). Both setState calls land in this one
            // handler, so the render that zeroes the offset already knows which
            // of the two it is.
            /*
             * Pull-to-close (rule 1.16), checked BEFORE the commit and only at
             * the head of the track.
             *
             * `raw` rather than `offset`: the clamp has already pinned `offset`
             * to zero here (slide 0 cannot travel downward), so the clamped
             * value can never express this gesture. `raw` is the finger's own
             * distance, which is what the visitor actually did.
             *
             * `direction` is necessarily 0 in this branch — a downward drag at
             * slide 0 has nowhere to go — so this cannot race the commit below.
             */
            if (currentRef.current === 0 && raw > PULL_TO_CLOSE_PX) {
                close();
                return;
            }
            const committed = direction !== 0 && go(direction);
            setChromeSnapAnimates(!committed);
        },
        [go, total, endDrag, close]
    );

    // A cancelled touch (an incoming call, a system gesture taking over) is not
    // a release: it expresses no destination, so the track returns and nothing
    // commits.
    const onTouchCancel = useCallback(() => {
        // Guarded so a stray cancel with no gesture live does not schedule a
        // pointless re-render; the suppression capture itself is in `endDrag`.
        if (!dragRef.current) return;
        endDrag();
    }, [endDrag]);

    // Safety net for a navigation that arrives from somewhere else mid-drag (a
    // trackpad wheel while a finger is down is the plausible one). The handlers
    // above already zero the offset on their own paths; this guarantees the
    // transform can never be left composing a stale drag over a new index.
    useEffect(() => {
        if (dragRef.current === null) setDragOffset(0);
    }, [current]);

    /*
     * ── Control dead space: a tap in the rail that hits nothing does nothing ─
     *
     * Cross-viewer decision, 2026-09-10. Asmita reported "the like icon acts as
     * a play/pause button" on all three viewers. On TikTok the readout itself
     * was already innocent — measured live at both viewports on three
     * consecutive slides, a tap on the heart or its count hit the readout and
     * left playback alone — but Facebook and Instagram traced the report to a
     * NEAR MISS: a tap in the 18px gap BETWEEN rail items, or in the rail's own
     * 34px bottom padding, fell through to the gesture layer and toggled pause.
     * Aiming for the heart and hitting the gap is the same experience as the
     * heart pausing the video, and from the visitor's side it is indistinguishable.
     *
     * So the uniform contract is: inside the rail's own column, a tap that
     * lands on no control does NOTHING — it does not pause and it does not
     * close. Taps on the VIDEO still toggle, which is the whole gesture
     * grammar; and the views readout in the info block is dead space by the
     * same rule, because it is a metric with a name rather than body copy.
     *
     * This supersedes the round-3 reading of §6.3 recorded in this file's
     * sibling test ("the row stays non-interactive — it inherits the meta
     * block's inertness"). §6.3's "non-interactive chrome opts OUT of pointer
     * events" is still how the CSS is built — the rail stays
     * `pointer-events: none` with each item opting back in, so the gaps still
     * fall THROUGH to the gesture layer. What changes is what the gesture layer
     * does when a tap arrives from one of those gaps: it declines, instead of
     * treating it as a tap on the video. Doing it here rather than by making
     * the gaps solid keeps the swipe working — a `pointer-events: auto` column
     * would eat the vertical drag that starts on it, which is the cost §6.3's
     * rule was written to avoid in the first place.
     *
     * Geometry from `getBoundingClientRect()`, deliberately: it already
     * accounts for the rail reel's travel transform, so the check holds on the
     * post-swipe transformed state as well as at rest. A zero-size rect means
     * the element is not laid out on this viewport (the desktop column on
     * touch, and vice versa) and is skipped rather than matched.
     */
    const deadSpaceSelectors = [
        // Desktop: the whole column beside the frame — rail items, the 12px
        // gap, and the chevron cluster below it.
        '.sbtt-swipeview-railcol',
        // Touch: the in-frame overlay column, including its 34px bottom padding.
        '.sbtt-swipeview-rail',
        // The views readout's ink in the bottom info block. The ROW is
        // full-width and must not be used — that would kill tap-to-pause across
        // a 465px strip. The label is the ~65px the visitor can actually see.
        '.sbtt-swipeview-meta-views-label',
    ];

    const pointIsInControlDeadSpace = useCallback((x, y) => {
        const root = contentRef.current;
        if (!root || typeof x !== 'number' || typeof y !== 'number') return false;
        return deadSpaceSelectors.some((selector) => {
            const el = root.querySelector(selector);
            if (!el) return false;
            const r = el.getBoundingClientRect();
            if (!r.width || !r.height) return false;
            return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onGestureClick = useCallback((e) => {
        // Swallow exactly one synthesised click, the one belonging to a drag.
        // FIRST, and unchanged: the drag's click has to be consumed whether or
        // not it landed in dead space, or the next real tap inherits the flag.
        if (suppressTapRef.current) {
            suppressTapRef.current = false;
            return;
        }
        // A tap that fell through the rail's gaps or its bottom padding is not
        // a tap on the video.
        if (e && pointIsInControlDeadSpace(e.clientX, e.clientY)) return;
        togglePlay();
    }, [togglePlay, pointIsInControlDeadSpace]);

    // Assigned during render, AFTER the handlers above exist, so the stable
    // listeners in `bindGestures` always dispatch to this render's closures
    // without any of them needing to rebind.
    liveGestureHandlersRef.current = {
        wheel: onWheel,
        touchstart: onTouchStart,
        touchmove: onTouchMove,
        touchend: onTouchEnd,
        touchcancel: onTouchCancel,
    };

    // Space/mute/arrow navigation (spec §3.1). Tab/Shift+Tab and Escape are
    // left to Radix's own focus trap + onEscapeKeyDown — do not re-handle
    // them here.
    const onKeyDown = useCallback(
        (e) => {
            /*
             * Rule 1.17's superset. `j`/`k` are the reference's own additions
             * (and vim's convention: j down, k up); `PageUp`/`PageDown` are
             * §3.1's requirement. Both are pure ALIASES for one-slide
             * navigation — there is no third behaviour to get wrong.
             *
             * Case-insensitive on the letters, because a visitor with caps lock
             * on has not asked for different behaviour.
             */
            const key = e.key;
            const lower = typeof key === 'string' && key.length === 1 ? key.toLowerCase() : key;
            if (key === 'ArrowDown' || key === 'PageDown' || lower === 'j') {
                e.preventDefault();
                go(1);
            } else if (key === 'ArrowUp' || key === 'PageUp' || lower === 'k') {
                e.preventDefault();
                go(-1);
            } else if (e.key === ' ') {
                e.preventDefault();
                togglePlay();
            } else if (lower === 'm') {
                toggleMute();
            }
        },
        [go, togglePlay, toggleMute]
    );

    const { title, caption, author } = useMemo(() => describePost(posts[current], headerData), [posts, current, headerData]);
    const creator = useMemo(
        () => describeCreator(author, canLoadExternal, gdprAvatarPlaceholder),
        [author, canLoadExternal, gdprAvatarPlaceholder]
    );
    /*
     * Maintainer review 2026-08-25, item 6: a failed avatar load hides the disc
     * outright. `describeCreator` already omits it when there is no DATA; this
     * covers the other way it can end up empty — a URL that resolves to
     * nothing. The broken-image glyph a browser substitutes is worse than the
     * absence, and it lands inside the rail's dark 44px circle where it reads
     * as a deliberate placeholder, which is exactly what the omit rule exists
     * to prevent.
     *
     * Keyed on the src so a different avatar gets its own fresh attempt: one
     * creator's dead URL on a multi-source feed must not suppress the next
     * creator's working one.
     */
    const avatarSrc = creator ? creator.src : null;
    const [avatarFailed, setAvatarFailed] = useState(false);
    useEffect(() => {
        setAvatarFailed(false);
    }, [avatarSrc]);
    /*
     * Bound through a callback ref rather than an `onError` prop, for two
     * reasons that happen to point the same way:
     *
     *   - A failed load can COMPLETE before React ever attaches a handler — a
     *     cached 404 is the ordinary case — and `onError` then never fires at
     *     all, leaving the broken image in place. On attach the element can just
     *     be asked: `complete` with a zero `naturalWidth` is a load that has
     *     already failed. The listener alone would miss exactly that case.
     *   - `jsx-a11y/no-noninteractive-element-interactions` lists `onError`
     *     among the handlers it flags on an <img>. It is a resource event, not a
     *     mouse or keyboard interaction, so the warning is a false positive —
     *     but this repo carries no jsx-a11y suppressions at all, and adding its
     *     first one to silence a false positive is a worse precedent than using
     *     the callback-ref idiom this file already relies on for the non-passive
     *     wheel binding.
     *
     * The listener is deliberately NOT `{ once: true }` and the <img> carries no
     * `key`: React reuses the element when only `src` changes, so the single
     * listener bound on first attach keeps covering every later avatar. A
     * `key={avatarSrc}` remount was tried and removed — it re-ran the
     * already-failed check for each new URL, but a changed `src` starts a fresh
     * load that fires `error` from cache anyway, so the listener already had
     * that case and no test could tell the two apart.
     */
    const bindAvatarImg = useCallback((node) => {
        if (!node) return;
        if (node.complete && node.naturalWidth === 0) {
            setAvatarFailed(true);
            return;
        }
        node.addEventListener('error', () => setAvatarFailed(true));
    }, []);
    // §6.1 counts for the ACTIVE post, gated by the feed's existing tile
    // toggles (useSwipeViewer's `counts`).
    const swipeCounts = useMemo(() => describeCounts(posts[current], counts), [posts, current, counts]);

    /*
     * ── The creator's profile link, and the verified badge (TT-4) ───────────
     *
     * The handle, the Follow pill and the rail's avatar all point HERE, so it is
     * resolved once. `profile_deep_link` is the account's own canonical link —
     * the same field Header.js's profile button uses — with the @handle URL as
     * the fallback shape. Both go through `safeUrl` (§12): a field is not
     * trusted just because it arrived inside `headerData` rather than a post.
     *
     * THE VERIFIED BADGE NEVER RENDERS TODAY, and that is worth stating plainly
     * rather than leaving someone to discover it.
     *
     * TT-4 asks for a cyan verified glyph "when known", and nothing in the SB
     * relay's TikTok response carries a verification flag — the plugin's own
     * feed header does not show one either. So this reads both spellings the
     * platform uses on different endpoints and renders nothing when neither is
     * present, which is what "when known" licenses. It is wired rather than
     * omitted so that the day the relay does carry the field, the badge appears
     * without a second design pass; and it is recorded in the change log as an
     * open item rather than resolved quietly, per the decision log's rule that a
     * visible conflict is escalated and never settled in code.
     */
    const profileUrl = useMemo(() => {
        if (!author) return '';
        return safeUrl(author.profile_deep_link)
            || (author.username ? safeUrl(`https://www.tiktok.com/@${author.username}`) : '');
    }, [author]);
    const isVerified = !!(author && (author.is_verified || author.verified));

    /*
     * The post's own URL, scheme-checked per §12 exactly like the terminal
     * tier's link (swipeAdapters.js `safeUrl`) — a field is not trusted just
     * because it arrived inside a post.
     *
     * Read by four things now: the top row's "View on TikTok" pill, the rail's
     * comments link, the rail's "View" item, and `share`. Resolved once here
     * rather than per consumer so they cannot disagree about what is safe.
     */
    const permalink = useMemo(() => safeUrl(posts[current] && posts[current].share_url), [posts, current]);

    /*
     * ── Share (TT-3), and the toast it needs ────────────────────────────────
     *
     * The one genuinely interactive metric in the rail, and it branches on the
     * INPUT, not on what the browser happens to implement (Asmita, 2026-09-10).
     *
     *   DESKTOP — copy the link and say so. Never the share dialog.
     *   TOUCH   — the native share sheet, with the clipboard as its fallback.
     *
     * The gate is `isDesktopPointer()`, the same capability query the layout
     * uses, evaluated at click time. Gating on `navigator.share`'s PRESENCE (the
     * previous shape) is what put a share dialog on desktop: Chrome desktop has
     * the API, so "does it exist" answered yes on precisely the platform where
     * the dialog is unwanted. Capability, not availability.
     *
     * Every arm still ends somewhere useful — a share button that silently does
     * nothing is the same dishonesty the likes readout refuses — and the manual
     * arm is a real path rather than defensive padding: neither API exists in
     * this project's jsdom, or on any page served over plain HTTP.
     *
     * `AbortError` is swallowed on purpose: a visitor who opens the sheet and
     * dismisses it has not failed at anything, and falling through to the
     * clipboard would copy a link they just declined to send.
     *
     * `swipeview_shared` now fires only when something COMPLETED — a resolved
     * `share()` or a resolved clipboard write. It used to fire optimistically at
     * the moment of the call, which counted cancellations and rejections as
     * shares and made the one number this event exists to produce wrong. The
     * manual arm does not emit at all: showing a visitor a URL to copy by hand
     * is not a share, and claiming one would be the same overcount by a
     * different route. The single exception is documented at its call site.
     */
    const [toast, setToast] = useState('');
    const toastTimerRef = useRef(null);
    const showToast = useCallback((message) => {
        setToast(message);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(''), TOAST_MS);
    }, []);
    useEffect(() => () => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    }, []);

    /* The manual arm: the URL, in a toast, for the visitor to copy by hand. */
    const showCopyableUrl = useCallback((url) => showToast(sprintf(
        /* translators: %s: the post's URL */
        __('Copy this link: %s', 'sb-customizer'),
        url
    )), [showToast]);

    /*
     * Copy through the API, confirm on success, and report a refusal rather
     * than claiming one. Returns false when there is no clipboard API to try,
     * so the caller can fall through to the manual arm — distinguishing "could
     * not attempt" from "attempted and failed", which the toast wording relies
     * on.
     */
    const copyLink = useCallback((url) => {
        if (!navigator.clipboard || !navigator.clipboard.writeText) return false;
        let result;
        try {
            result = navigator.clipboard.writeText(url);
        } catch (e) {
            // Can throw synchronously under a permissions policy.
            return false;
        }
        const copied = () => {
            showToast(__('Link copied', 'sb-customizer'));
            emit('swipeview_shared', { index: currentRef.current, method: 'clipboard' });
        };
        if (result && typeof result.then === 'function') result.then(copied, () => showCopyableUrl(url));
        else copied();
        return true;
    }, [showToast, showCopyableUrl, emit]);

    const share = useCallback(() => {
        const url = permalink;
        if (!url) return;

        /*
         * DESKTOP: the clipboard, and nothing else. `navigator.share` is not
         * consulted even where it exists — the point of the branch.
         */
        if (isDesktopPointer()) {
            if (!copyLink(url)) showCopyableUrl(url);
            return;
        }

        /* TOUCH: the sheet, then the clipboard, then the URL. */
        try {
            if (navigator.share) {
                const result = navigator.share({ title, text: caption, url });
                if (result && typeof result.then === 'function') {
                    result.then(
                        () => emit('swipeview_shared', { index: currentRef.current, method: 'web-share' }),
                        (err) => {
                            // Cancelled: silent, and no event. Nothing failed
                            // and nothing was shared.
                            if (err && err.name === 'AbortError') return;
                            if (!copyLink(url)) showCopyableUrl(url);
                        }
                    );
                } else {
                    /*
                     * Older implementations return undefined rather than a
                     * promise, so completion is unobservable. This is the one
                     * place the event stays optimistic, and it is a narrow,
                     * shrinking case rather than the default the whole chain
                     * used to be.
                     */
                    emit('swipeview_shared', { index: currentRef.current, method: 'web-share' });
                }
                return;
            }
        } catch (e) {
            // Blocked outright by a permissions policy — fall through.
        }
        if (!copyLink(url)) showCopyableUrl(url);
    }, [permalink, title, caption, copyLink, showCopyableUrl, emit]);

    // §6.1 "… more" caption expander (maintainer request 2026-08-19), the
    // §6.3-safe way: the caption TEXT never becomes tappable (the caption-band
    // lesson — a wide/tall tappable caption swallows play/pause taps and
    // swipes); ONLY the explicit more/less button opts into pointer events,
    // and the expanded caption owns its own scroll surface, which cannot feed
    // the sibling gesture layer. Expansion never survives a slide change.
    const [captionExpanded, setCaptionExpanded] = useState(false);
    const [captionOverflows, setCaptionOverflows] = useState(false);
    // STATE-backed callback ref, not useRef: Radix mounts the dialog content a
    // commit later than this component's first effect pass, so a plain ref is
    // still null when a [deps]-driven measure effect runs and (deps unchanged)
    // it never re-measures — the same Radix-mount trap the wheel binding hit.
    // Holding the node in state makes its arrival re-run the measure below.
    const [captionNode, setCaptionNode] = useState(null);
    useEffect(() => {
        setCaptionExpanded(false);
    }, [current]);
    useEffect(() => {
        if (captionExpanded) return; // measure only in the clamped state
        setCaptionOverflows(!!captionNode && captionNode.scrollHeight > captionNode.clientHeight + 1);
    }, [captionNode, caption, current, captionExpanded]);

    /*
     * The platform CTA used to be FALLBACK-ONLY, driven off the sampled tier
     * index: absent while the media tier played, present on the embed and
     * poster tiers. That was a defensible reading of "the escape hatch for a
     * slide we cannot play", and TT-6 replaces it — the design carries a
     * labelled "View on TikTok" pill in the top row on every slide, and §6.1's
     * rule 6-f independently requires this CTA to be permanent chrome present
     * on EVERY tier, never re-rendered or relocated as the chain escalates. So
     * `showPlatformCta` and its tier gate are gone; the pill's only condition is
     * having a permalink to send the visitor to.
     */
    const canGoPrev = current > 0;
    const canGoNext = current < total - 1;

    /*
     * SMASH-1978 (Maintainer decision, 2026-08-27): the per-reel chrome TRAVELS
     * with its reel during a drag, instead of staying pinned to the screen frame
     * while the reel slides out from under it — which is what put reel N's stats
     * over reel N+1's incoming video for the whole gesture.
     *
     * ── The mechanism, and why it is this one ────────────────────────────────
     *
     * The chrome stays exactly where it is — a sibling of the track in the
     * chrome layer — and takes the live drag delta as its own transform. It is
     * the SAME `dragOffset` the track composes, deliberately shared rather than
     * recomputed: the offset is already end-clamped (`clampDragOffset`), and a
     * second derivation is how the two would eventually disagree about a clamp
     * and drift apart by a few pixels at the ends of the track.
     *
     * The delta alone is the correct mirror, not the track's whole transform.
     * The track sits at `-current * 100% + dragOffset`; its active slide
     * therefore sits at `dragOffset` relative to the frame, and that is what
     * this chrome — which describes exactly that one slide — has to match.
     *
     * §6.3 SAFETY, which is the constraint that ruled out the obvious
     * alternative. Moving these controls INTO the slide track is how Instagram
     * gets travel (per-slide chrome, `sbi-qs-chrome` inside each `sbi-qs-post`),
     * and it is not available here for a reason that has nothing to do with
     * stacking: `.sbtt-swipeview-rail-nav`'s desktop chevrons are
     * `position: fixed`, which escapes `.sbtt-swipeview-content`'s
     * `overflow: hidden` ONLY while no ancestor establishes a containing block
     * for fixed descendants. The track carries a transform, so anything inside
     * it becomes the chevrons' containing block and they snap back into the
     * column and get clipped — the exact failure SwipeView.test.js's
     * "nothing on the overlay/content chain may become a containing block"
     * block already guards. That is also why the transform below goes on
     * `.sbtt-swipeview-rail-reel` (the reel-specific controls) and NOT on
     * `.sbtt-swipeview-rail` itself: the nav cluster is a sibling of that
     * wrapper, so it keeps an untransformed ancestor chain. The chevrons are
     * viewport nav rather than reel chrome anyway — native TikTok's sit outside
     * the column and do not move with the video.
     *
     * Everything else stays pinned, and each for a reason: the counter and close
     * are viewer chrome with no per-reel content, the progress bar is a
     * frame-edge line that native TikTok also leaves pinned, and the unmute pill
     * is an affordance about the session's audio permission (and is cleared on
     * every commit regardless).
     *
     * ── What happens at the commit, stated rather than glossed ───────────────
     *
     * There is ONE chrome element, not one per slide, so it cannot both travel
     * out with the outgoing reel and arrive with the incoming one. On a commit it
     * lands at rest in the same paint that swaps its content, while the track
     * animates the remaining distance — which is precisely what this viewer
     * ALREADY does on every wheel, keyboard and chevron navigation. So the
     * commit is not a new visual grammar being invented here; it is the existing
     * one, and routing the drag-commit through `go` (see `onTouchEnd`) is what
     * keeps it identical. The change is confined to the drag itself, where the
     * chrome was previously the only thing on screen not moving.
     *
     * Rejected: jumping the chrome to the incoming reel's off-frame position and
     * animating it home. That is the fuller grammar, but it needs a
     * jump-then-animate across two paints (rAF) to keep the transition from
     * collapsing, and a dropped frame there shows the chrome at the wrong
     * position rather than merely un-animated. Not worth it to make a commit
     * differ from every other navigation path in the viewer.
     */
    const chromeTravelStyle = {
        transform: `translateY(${dragOffset}px)`,
        // Borrowed from the track verbatim (`reducedMotion || dragging`), plus
        // the commit case. Under reduced motion the follow stays — direct
        // manipulation is not motion the visitor did not ask for — and the snap
        // is instant, exactly as the track's own duration already resolves.
        transitionDuration: reducedMotion || dragging || !chromeSnapAnimates ? '0ms' : `${TRANSITION_MS}ms`,
    };

    const handleOpenChange = (open) => {
        if (!open) close();
    };

    return (
        <DialogRoot open onOpenChange={handleOpenChange}>
            <DialogPortal>
                <DialogOverlay className="sbtt-swipeview-overlay">
                    <DialogContent
                        ref={contentRef}
                        className="sbtt-swipeview-content"
                        /*
                         * SMASH-1978 — on DialogContent rather than DialogRoot for
                         * two reasons, the first of them absolute: `DialogRoot` is
                         * `RadixDialog.Root`, a context provider that renders no DOM
                         * node at all, so there is nothing there to hang an attribute
                         * on. This element is also the right one on its own merits —
                         * it carries `role="dialog"`, it owns the `onKeyDown` below
                         * that implements most of what the attribute advertises, and
                         * `onOpenAutoFocus` focuses it explicitly on open. Since
                         * `aria-keyshortcuts` is announced when focus reaches its
                         * host, putting it on the initial focus target is what makes
                         * it heard at all. Radix gives Content `tabIndex="-1"`, so
                         * this stays programmatically focusable and adds no Tab stop.
                         */
                        /*
                         * §7.1 / rule 1.18. Radix's focus trap already keeps Tab
                         * inside the dialog, which is why this went unnoticed —
                         * but `aria-modal` is what tells a screen reader to stop
                         * exposing the rest of the page in its VIRTUAL cursor
                         * and browse modes, which a focus trap does not touch.
                         * Without it an AT user can read the whole feed behind an
                         * open viewer while Tab correctly refuses to go there.
                         *
                         * Found by browser measurement (it read `null`), not by
                         * the suite: the survey had it recorded as a pre-existing
                         * gap and nothing was asserting it either way.
                         */
                        aria-modal="true"
                        aria-keyshortcuts={KEY_SHORTCUTS}
                        /*
                         * SMASH-1853 — the explicit opt-out, which is Radix's own
                         * documented remedy (its warning names this exact prop).
                         * Radix's `DialogContent` sets `aria-describedby` to a
                         * generated id unconditionally, BEFORE spreading caller
                         * props, and only `<Dialog.Description>` ever renders that
                         * id. This viewer renders no Description, so the attribute
                         * shipped pointing at nothing: AT was told a description
                         * existed and resolved it to empty, and the console warning
                         * — NOT dev-gated in @radix-ui/react-dialog@1.1.15, so it
                         * is in the production bundle — fired for every visitor.
                         *
                         * Opting out rather than writing a Description is the
                         * deliberate half. Focus lands on this element on open, so
                         * a description would be announced in the same breath as
                         * the name ("Swipe view", via the Title below) and
                         * `aria-keyshortcuts` above. Both candidate contents are
                         * already covered: the operational half IS
                         * `aria-keyshortcuts`, added directly above for exactly
                         * that job, and the rest would restate the name. SMASH-1978
                         * held that string to one token per action so the opening
                         * announcement stays short; a description re-covering the
                         * same ground would spend that budget twice.
                         *
                         * Matches the two existing consumers of this primitive,
                         * which already pass the prop explicitly so that omitting
                         * it IS the opt-out: ModalContainer.js (see its docblock on
                         * `describedBy`) and a11y/Dialog.js.
                         */
                        aria-describedby={undefined}
                        onKeyDown={onKeyDown}
                        onOpenAutoFocus={(e) => {
                            e.preventDefault();
                            if (contentRef.current) contentRef.current.focus();
                        }}
                        onCloseAutoFocus={(e) => {
                            // §7.1: focus restoration target is passed in explicitly by
                            // the opener, never inferred — a click on a non-focusable
                            // tile would otherwise leave focus on <body>.
                            e.preventDefault();
                            if (opener && opener.focus) opener.focus();
                        }}
                    >
                        <RadixVisuallyHidden asChild>
                            <DialogTitle>{__('Swipe view', 'sb-customizer')}</DialogTitle>
                        </RadixVisuallyHidden>

                        {/* Window-anchored close, top-LEFT (rule 1.4).
                          *
                          * Moved OUT of the frame in the 2026-09-08 restyle, and that is
                          * the point rather than a side effect: close acts on the VIEWER,
                          * so it is window chrome — the ratified "anchor a control by what
                          * it acts on". Everything else this restyle adds is frame chrome,
                          * which makes this the one control that is a sibling of the stage
                          * rather than a child of the frame.
                          *
                          * It also moved from top-RIGHT to top-LEFT, and the frame's own
                          * top-left is now deliberately EMPTY (rule 1.5): no wordmark, no
                          * counter, nothing. The design leaves it blank twice over — the
                          * author removed a wordmark from it and never added a counter —
                          * so nothing may be put back there.
                          *
                          * Not wrapped in DialogClose: `open` above is hardcoded `true`
                          * (mount/unmount is driven externally by the caller's isSwipeOpen
                          * state), so calling close() directly is the only path and there
                          * is no double-fire risk to guard against.
                          *
                          * `data-tip` is revealed by CSS on pointer devices only (rules
                          * 1.9, 1.11) and carries the KEY alongside the label, because the
                          * key is the half a pointer user cannot discover by looking. The
                          * double space before "Esc" is the design's own separator. */}
                        <button
                            type="button"
                            className="sbtt-swipeview-chrome sbtt-swipeview-close sb-button-reset"
                            onClick={close}
                            aria-label={__('Close swipe view', 'sb-customizer')}
                            data-tip={__('Close  Esc', 'sb-customizer')}
                            data-tip-pos="right"
                        >
                            <SwipeCloseIcon />
                        </button>

                        {/* §7.1 position announcement, AT-ONLY (rule 1.8).
                          *
                          * The VISIBLE "n / total" counter is gone. The design carries no
                          * counter on any platform at either viewport — measured zero
                          * counter nodes across six combos — and it leaves the frame's
                          * top-left blank on purpose, so porting the design means removing
                          * ours rather than repositioning it.
                          *
                          * §6.1 lists a visible slide counter among its seven exhaustive
                          * uniform guarantees, so dropping it outright is a normative
                          * conflict and NOT an implementer's call. This is the synthesis
                          * the decision log settles on (rule 1.8): keep §7.1's position
                          * ANNOUNCEMENT in AT-only form, which costs nothing visually and
                          * preserves the blank top-left, and raise §6.1's visible-counter
                          * guarantee as a spec amendment — a docs PR, not this wave. The
                          * conflict is recorded and escalated, per §6.4, not resolved
                          * quietly in code.
                          *
                          * Reads against the SNAPSHOT (`total` is `posts.length`, captured
                          * once at open), never the whole feed — §2.3/§2.j. */}
                        <RadixVisuallyHidden asChild>
                            <div className="sbtt-swipeview-position" role="status" aria-live="polite">
                                {sprintf(
                                    /* translators: 1: current slide number, 2: total slides */
                                    __('%1$s of %2$s', 'sb-customizer'),
                                    current + 1,
                                    total
                                )}
                            </div>
                        </RadixVisuallyHidden>

                        {/* §6.4 deviation, recorded per its own rule ("escalated rather
                          * than silently resolved"): removing the persistent play/pause
                          * BUTTON (Task A, visual treatment) also removed the only
                          * AT-reachable surface reflecting play state — §6.1's "Play/
                          * pause: reflects actual player state" is behaviour and stays
                          * normative regardless of how it's rendered. This offscreen
                          * live region is the cheap fix Rex's review asked for: it
                          * carries the SAME `chrome.playing` value the tap-flash echoes
                          * visually, sourced from the same 250ms-polled real state as
                          * everything else in this block, so sighted and AT users get
                          * the same information through different channels. Suppressed
                          * entirely when the active tier has no playPause capability
                          * (§6.2) rather than announcing a state that isn't real. */}
                        <RadixVisuallyHidden asChild>
                            <div className="sbtt-swipeview-playstate" role="status" aria-live="polite">
                                {chrome.caps.playPause
                                    ? chrome.playing
                                        ? __('Playing', 'sb-customizer')
                                        : __('Paused', 'sb-customizer')
                                    : ''}
                            </div>
                        </RadixVisuallyHidden>

                        {/* First-run coach overlay (rule 1.9). Window-level and
                          * full-bleed, because it dims the whole viewer rather than
                          * describing anything inside the frame.
                          *
                          * `aria-hidden` and `pointer-events: none`: it is a
                          * pictogram over a scrim, it advertises a gesture rather
                          * than offering a control, and an AT user already has
                          * `aria-keyshortcuts` on the dialog naming the real
                          * bindings. At the default `auto` a full-bleed box would
                          * swallow every gesture it exists to teach. */}
                        {coachVisible && (
                            <div className="sbtt-swipeview-coach" aria-hidden="true">
                                <SwipeCoachHandIcon />
                                <span>{__('Swipe up for next', 'sb-customizer')}</span>
                            </div>
                        )}

                        {/* Share feedback (TT-3). Window-anchored, because it reports on
                          * an ACTION rather than describing a reel — and outside the frame
                          * so a long URL on the manual arm is not clipped by it.
                          *
                          * `role="status"` + polite, not `alert`: a copied link is a
                          * confirmation, and an assertive region would preempt the
                          * position announcement mid-swipe. Mounted only while a message
                          * is live so the region is not repeatedly re-announced empty. */}
                        {toast && (
                            <div className="sbtt-swipeview-toast" role="status" aria-live="polite">
                                {toast}
                            </div>
                        )}

                        <div className="sbtt-swipeview-shell">
                            <div className="sbtt-swipeview-frame" data-paused={paused ? 'true' : 'false'}>
                                <div className="sbtt-swipeview-viewport">
                                    <div
                                        className="sbtt-swipeview-track"
                                        style={{
                                            /*
                                             * SMASH-1978: the slide index in PERCENT, the live
                                             * finger delta in PX, composed in one calc().
                                             *
                                             * Keeping the two in their natural units is the point.
                                             * The index has to be percentage-based — each slot is
                                             * positioned at `top: N * 100%` and the track has no
                                             * pixel height of its own — while the drag is a real
                                             * measured pixel distance and converting it to a
                                             * percentage would mean re-deriving the viewport height
                                             * on every move just to divide it straight back out.
                                             */
                                            transform: `translateY(calc(-${current * 100}% + ${dragOffset}px))`,
                                            /*
                                             * No transition while the finger is down: a transition
                                             * here would make the track chase the thumb instead of
                                             * following it, which is the whole difference between
                                             * direct manipulation and animation. On release the
                                             * normal duration returns in the SAME render that zeroes
                                             * the offset, so the snap animates.
                                             *
                                             * Under reduced motion the release is already
                                             * non-animated, so drag and snap are functionally
                                             * identical — the follow-drag itself stays, because it
                                             * is direct manipulation rather than motion the visitor
                                             * did not ask for.
                                             */
                                            transitionDuration: reducedMotion || dragging ? '0ms' : `${TRANSITION_MS}ms`,
                                        }}
                                    >
                                        {slotAssignment.map((trackIndex, slotPos) => (
                                            <SwipeSlot
                                                key={slotPos}
                                                ref={(handle) => {
                                                    slotRefs[slotPos] = handle;
                                                }}
                                                post={trackIndex !== null ? posts[trackIndex] : null}
                                                trackIndex={trackIndex}
                                                isActive={trackIndex === current}
                                                unmuted={unmuted}
                                                chain={chain}
                                                startTimeoutMs={START_TIMEOUT_MS}
                                                onAnalytics={handleSlotAnalytics}
                                            />
                                        ))}
                                    </div>

                                    {/* §3.3/§7.1: the viewer owns every gesture; this layer is
                                      * hidden from AT and not focusable — real controls live in
                                      * the chrome below. */}
                                    {/* The whole touch sequence and wheel are bound NATIVELY by
                                      * `bindGestures` (passive: false) rather than through props —
                                      * see its comment. `onClick` stays a prop: click is not a
                                      * passive-listener category, and it is a tap rather than part
                                      * of the drag. It is routed through `onGestureClick` so the
                                      * synthesised click that can follow a drag does not also
                                      * toggle playback. */}
                                    <div
                                        ref={bindGestures}
                                        className="sbtt-swipeview-gestures"
                                        aria-hidden="true"
                                        onClick={onGestureClick}
                                    />
                                </div>

                                {/* The pause glyph (rule 1.10) — a play triangle shown for as
                                  * long as the slide is paused, replacing the old transient
                                  * tap-flash.
                                  *
                                  * ALWAYS a play triangle, never a pause bar: it shows what
                                  * pressing will DO, and it only ever appears while paused, so
                                  * there is no second state for it to depict. That is why
                                  * `SwipePauseIcon` is no longer used here.
                                  *
                                  * Decorative — `aria-hidden`, `pointer-events: none` — and it
                                  * must stay both. It covers the middle of the frame, so at the
                                  * default `auto` it would swallow the very tap-to-resume it is
                                  * advertising. The AT surface for play state is the offscreen
                                  * live region above, not this. */}
                                {flashOn && (
                                    <div className="sbtt-swipeview-flash" aria-hidden="true">
                                        <SwipePlayIcon />
                                    </div>
                                )}

                                {/* ── The in-frame top row (rules 1.5, 1.6, 1.7, TT-6) ──
                                  *
                                  * `[blank left] … [mute][View on TikTok]`, right-aligned.
                                  * The frame's top-LEFT stays deliberately empty — close is
                                  * window chrome and lives outside this frame entirely.
                                  *
                                  * Both controls MOVED here from the rail, and both moves are
                                  * the design's: the sound toggle was a 44px disc at the
                                  * bottom-right (reversing the 2026-08-28 placement), and the
                                  * CTA was a fallback-only brand-mark disc below it. */}
                                <div className="sbtt-swipeview-chrome sbtt-swipeview-toprow">
                                    {/* The mute button, and the one control the walkthrough
                                      * spends 25 seconds on.
                                      *
                                      * PAINT reads the REAL sampled `chrome.muted`
                                      * (swipeAdapters' `isMuted()` via the 250ms poll), never
                                      * the `unmuted` intent — rule 1.7(a). The two diverge for
                                      * as long as a rejected `attemptPlay()` retry has left the
                                      * element muted, and a chip painted from intent would sit
                                      * there dark, claiming sound was on while the video played
                                      * silently. `data-muted` is what drives the white/dark
                                      * inversion in CSS.
                                      *
                                      * §6.2 / rule 1.20: DISABLED, never omitted, on a tier
                                      * that cannot carry audio. The embed and poster tiers
                                      * report `caps.mute` false, and `m` no-ops there too.
                                      *
                                      * No `aria-pressed`. Combining a CHANGING accessible name
                                      * (Mute <-> Unmute) with `aria-pressed` is the WAI-ARIA APG
                                      * anti-pattern this button used to have:
                                      * `aria-pressed={!chrome.muted}` announced "Mute, pressed"
                                      * while unmuted, which reads as "muting IS engaged" — the
                                      * opposite of the true state. The APG's fix is to pick one,
                                      * and §6.1 specifies "label switches between Mute and
                                      * Unmute", so the changing name is the normative half and
                                      * `aria-pressed` is the one that goes. */}
                                    <button
                                        type="button"
                                        className={'sbtt-swipeview-mute sb-button-reset' + (muteHinting ? ' sbtt-swipeview-mute--hinting' : '')}
                                        onClick={toggleMute}
                                        disabled={!chrome.caps.mute}
                                        data-muted={chrome.muted ? 'true' : 'false'}
                                        aria-label={chrome.muted ? __('Unmute', 'sb-customizer') : __('Mute', 'sb-customizer')}
                                    >
                                        {/* Glyph names describe the STATE shown, not the action
                                          * that leaves it — the accessible name above is the
                                          * action, and the two must not be confused. */}
                                        {chrome.muted ? <SwipeSoundOffIcon /> : <SwipeSoundOnIcon />}
                                        {/*
                                          * "Tap to unmute", sliding out of the button (rule 1.7).
                                          *
                                          * `aria-hidden` because the button's own `aria-label`
                                          * already says "Unmute" whenever this is visible —
                                          * announcing both would read the same instruction twice
                                          * for one control. This is a VISUAL affordance for a
                                          * sighted visitor who cannot tell silence from a muted
                                          * player; an AT user has the name.
                                          *
                                          * Always rendered, never conditionally mounted: the
                                          * label slides out of and back into the button, and a
                                          * `max-width` transition needs both ends of the
                                          * animation to exist. Mounting it on `muteHinting`
                                          * would make it appear at full width and jump.
                                          */}
                                        <span className="sbtt-swipeview-mute-label" aria-hidden="true">
                                            {__('Tap to unmute', 'sb-customizer')}
                                        </span>
                                    </button>

                                    {/* "View on TikTok" (TT-6). Desktop only — the touch layout
                                      * moves it into the rail as a labelled item (TT-2), which
                                      * is what Aman states on the video.
                                      *
                                      * PRESENT ON EVERY TIER, unlike the disc it replaces. That
                                      * disc was gated on `chrome.tierIndex > 0`, i.e. shown only
                                      * once a slide had fallen back — which was a reasonable
                                      * reading of "the escape hatch for a slide we cannot play",
                                      * but §6.1's rule 6-f actually requires this CTA to be
                                      * permanent chrome present on every tier, inside the focus
                                      * trap, and centre-hit-tested on every tier. Following the
                                      * design satisfies that as a side effect.
                                      *
                                      * Omitted entirely when there is no permalink to send the
                                      * visitor to, rather than rendered inert. */}
                                    {permalink && (
                                        <a
                                            className="sbtt-swipeview-cta"
                                            href={permalink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <SwipeTikTokIcon />
                                            {__('View on TikTok', 'sb-customizer')}
                                        </a>
                                    )}
                                </div>

                                {/* ── Bottom info (TT-4, TT-5) ──
                                  *
                                  * Was a bold `@handle` over a 3-line caption, with a views
                                  * row above it. Becomes the design's two-part block: a row
                                  * of handle + verified + Follow, and a 2-line caption with
                                  * a "more" expander below it.
                                  *
                                  * NO AVATAR here — it is in the rail (TT-2). And no
                                  * views/meta row: views left the viewer entirely (TT-3).
                                  *
                                  * Travels with its reel during a drag (SMASH-1978) — see
                                  * `chromeTravelStyle`. The block is non-interactive text
                                  * plus three shrink-wrapped controls, so the transform has
                                  * nothing to reposition but itself. */}
                                {(title || caption || swipeCounts.views !== null) && (
                                    <div className="sbtt-swipeview-chrome sbtt-swipeview-meta" style={chromeTravelStyle}>
                                        {/* ── Views (parity, 2026-09-08) ──
                                          *
                                          * The design has NO slot for a view count — TikTok's
                                          * own player has none either — but every other
                                          * surface in this plugin shows one: the tiles under
                                          * `postElements` 'views', and the lightbox in its
                                          * footer stats row. Hiding it only in the viewer made
                                          * the swipe layout the odd one out.
                                          *
                                          * PLACEMENT is the previously ratified bottom-left
                                          * meta position, and the lightbox independently
                                          * agrees with it: its stats row sits below the
                                          * caption in the footer, which is the analogous
                                          * position to this block.
                                          *
                                          * WHERE WE DIVERGE FROM THE LIGHTBOX, recorded for
                                          * Aman: the lightbox PAIRS views and likes in one
                                          * row. Ours splits them, because TT-2 puts likes in
                                          * the rail and that is the design's own placement.
                                          * Pairing them here would have meant overruling the
                                          * rail as well, which is a bigger call than parity
                                          * asked for.
                                          *
                                          * Reuses the plugin's own `ViewsIcon` rather than a
                                          * swipe-scoped glyph, so the viewer, the tiles and
                                          * the lightbox read as one plugin. */}
                                        {swipeCounts.views !== null && (
                                            <span className="sbtt-swipeview-meta-views">
                                                {/* The abbreviation is VISUAL only — hidden
                                                  * from AT so the exact figure below is
                                                  * announced once rather than twice. */}
                                                <span aria-hidden="true" className="sbtt-swipeview-meta-views-label">
                                                    <ViewsIcon />
                                                    {sprintf(_n('%s view', '%s views', swipeCounts.views, 'sb-customizer'), abbreviateCount(swipeCounts.views))}
                                                </span>
                                                <RadixVisuallyHidden>
                                                    {sprintf(_n('%s view', '%s views', swipeCounts.views, 'sb-customizer'), exactCount(swipeCounts.views))}
                                                </RadixVisuallyHidden>
                                            </span>
                                        )}
                                        {title && (
                                            <div className="sbtt-swipeview-meta-row">
                                                {/* The handle is a LINK to the profile now, where it
                                                  * used to be a bold `<b>`. Aman's stated rationale
                                                  * for the CTA density is explicitly ours rather
                                                  * than a platform imitation — "our users might
                                                  * appreciate having CTA almost everywhere" — on the
                                                  * reasoning that an embedded feed exists to send
                                                  * traffic. So treat any instinct to thin these out
                                                  * as a change of intent needing his sign-off.
                                                  *
                                                  * `width: max-content` in CSS, not a full-width
                                                  * flex child: a strip spanning the frame would kill
                                                  * tap and swipe across the whole band, which is
                                                  * §6.3's most-repeated defect in this file. */}
                                                {profileUrl ? (
                                                    <a
                                                        className="sbtt-swipeview-meta-handle"
                                                        href={profileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {title}
                                                        {isVerified && <SwipeVerifiedIcon />}
                                                    </a>
                                                ) : (
                                                    <span className="sbtt-swipeview-meta-handle">
                                                        {title}
                                                        {isVerified && <SwipeVerifiedIcon />}
                                                    </span>
                                                )}

                                                {/* Follow — solid TikTok red, and the one control in
                                                  * this row that is unambiguously an outbound CTA.
                                                  *
                                                  * A LINK, not a button, and that is the honest
                                                  * shape: an embedded feed cannot follow on the
                                                  * visitor's behalf, so this sends them to the
                                                  * profile where the real Follow button lives. The
                                                  * `aria-label` names the destination for exactly
                                                  * that reason — "Follow" alone would promise an
                                                  * in-page action, which is the same dishonesty the
                                                  * rail's likes readout refuses. */}
                                                {profileUrl && (
                                                    <a
                                                        className="sbtt-swipeview-meta-follow"
                                                        href={profileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={sprintf(
                                                            /* translators: %s: account handle, including the leading @ */
                                                            __('Follow %s on TikTok', 'sb-customizer'),
                                                            title
                                                        )}
                                                    >
                                                        {__('Follow', 'sb-customizer')}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {caption && (
                                            <span
                                                id="sbtt-swipeview-caption"
                                                ref={setCaptionNode}
                                                className={'sbtt-swipeview-meta-caption' + (captionExpanded ? ' sbtt-swipeview-meta-caption--expanded' : '')}
                                            >
                                                {caption}
                                            </span>
                                        )}
                                        {caption && captionOverflows && (
                                            <button
                                                type="button"
                                                className="sbtt-swipeview-caption-more sb-button-reset"
                                                aria-expanded={captionExpanded}
                                                aria-controls="sbtt-swipeview-caption"
                                                onClick={() => setCaptionExpanded((v) => !v)}
                                            >
                                                {captionExpanded ? __('less', 'sb-customizer') : __('more', 'sb-customizer')}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {chrome.caps.progress && (
                                    <div
                                        className="sbtt-swipeview-chrome sbtt-swipeview-progress"
                                        role="progressbar"
                                        aria-label={__('Playback progress', 'sb-customizer')}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={Math.round(chrome.progress)}
                                    >
                                        <span
                                            style={{
                                                width: `${chrome.progress}%`,
                                                // Rule 1.12's synchronous write. Inline rather
                                                // than a class because the value is per-render
                                                // and a class would have to fight the
                                                // stylesheet's own duration.
                                                transitionDuration: reducedMotion || progressInstant ? '0ms' : '200ms',
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Rail column (rules 1.3, 1.11) — OUTSIDE the frame on
                              * desktop, 16px to its right, and a zero-box on touch
                              * (`display: contents`) where the rail becomes an in-frame
                              * overlay instead.
                              *
                              * The chevrons used to be `position: fixed` so they could
                              * escape the old 480px column’s `overflow: hidden` while
                              * still being DOM descendants of the rail — which is what
                              * earned this file three separate warnings that nothing on
                              * the overlay/content chain may become a containing block
                              * for fixed descendants. None of that applies any more: the
                              * column they had to escape is gone, and they are ordinary
                              * grid items in row 2 of this column now. */}
                            <div className="sbtt-swipeview-railcol">
                                {/* Viewport nav, NOT reel chrome: deliberately outside the
                                  * travel wrapper above. Native TikTok's chevrons sit beside
                                  * the column and stay put while the video moves, and this
                                  * cluster is `position: fixed` to get there — a transformed
                                  * ancestor would become its containing block and clip it
                                  * back inside the column (SMASH-1978). */}
                                <div className="sbtt-swipeview-rail-nav">
                                    <button
                                        type="button"
                                        className="sb-button-reset"
                                        onClick={() => go(-1)}
                                        disabled={!canGoPrev}
                                        aria-label={__('Previous video', 'sb-customizer')}
                                        /*
                                         * Rule 1.9 asked for tooltips on close AND BOTH
                                         * chevrons; only close got one, which is what Asmita
                                         * is reporting ("on hover on Aman's prototype it
                                         * shows labels for keys Up and Down"). The mechanism
                                         * was already correct and working on close — measured
                                         * live, opacity 0 -> 1 on hover AND on focus-visible —
                                         * so this was a missing attribute, not a broken rule.
                                         *
                                         * The KEY is the load-bearing half of the copy. A
                                         * pointer user can see what a chevron does; the
                                         * shortcut is the part they cannot discover by
                                         * looking, which is why the design puts it here
                                         * rather than settling for an `aria-label`.
                                         *
                                         * Both bindings are named because rule 1.17 made them
                                         * a superset — advertising only the arrow would teach
                                         * half the interface.
                                         */
                                        data-tip={__('Previous  ↑ / K', 'sb-customizer')}
                                        data-tip-pos="right"
                                    >
                                        <ChevronUpIcon />
                                    </button>
                                    <button
                                        type="button"
                                        className="sb-button-reset"
                                        onClick={() => go(1)}
                                        disabled={!canGoNext}
                                        aria-label={__('Next video', 'sb-customizer')}
                                        data-tip={__('Next  ↓ / J', 'sb-customizer')}
                                        data-tip-pos="right"
                                    >
                                        <ChevronDownIcon />
                                    </button>
                                </div>
                                <div className="sbtt-swipeview-chrome sbtt-swipeview-rail">
                                    {/* SMASH-1978: the REEL-SPECIFIC half of the rail —
                                      * avatar, counts, sound, platform CTA — grouped so it
                                      * can travel with its reel during a drag while the
                                      * desktop nav cluster below stays put.
                                      *
                                      * The split is not cosmetic. `.sbtt-swipeview-rail-nav`
                                      * is `position: fixed` (it paints outside the column),
                                      * and a transformed ANCESTOR would become its
                                      * containing block and pull it back inside the clip —
                                      * so the transform has to stop short of it. Keeping
                                      * this wrapper INSIDE `.sbtt-swipeview-rail` is equally
                                      * deliberate: the (0,1,1) round-control rule and the
                                      * `pointer-events: auto` opt-in are both descendant
                                      * selectors on the rail, and this file's history has
                                      * two shipped bugs from controls losing that lineage.
                                      * A plain wrapper inherits the rail's
                                      * `pointer-events: none`, so it adds no new dead space
                                      * of its own (Rex finding #3's rule). */}
                                    <div className="sbtt-swipeview-rail-reel" style={chromeTravelStyle}>
                                        {/* Creator avatar (maintainer review 2026-08-24, item 5).
                                          * Native rail anatomy is avatar -> like -> comment -> share,
                                          * with two deliberate deviations, both maintainer calls:
                                          *
                                          *   - NO red follow-+ badge. An embedded feed cannot follow on
                                          *     a visitor's behalf, and a badge that looks tappable but
                                          *     isn't is the same dishonesty the counts cluster below
                                          *     already refuses ("a heart styled like TikTok's own
                                          *     tappable heart would promise what the UI cannot
                                          *     deliver").
                                          *   - OMITTED entirely when there is no avatar data, never
                                          *     stood in for with a generic disc. describeCreator
                                          *     returns null and this renders nothing, so the rail
                                          *     closes up exactly as it does for a missing metric.
                                          *     `avatarFailed` extends that to a URL that resolves to
                                          *     nothing (item 6): a browser's broken-image glyph inside
                                          *     the rail's dark circle reads as exactly the deliberate
                                          *     placeholder the rule above forbids.
                                          *
                                          * Cascade, decided not discovered: this is a new <a> inside
                                          * `.sbtt-swipeview-rail`, so the (0,1,1) round-control rule
                                          * DOES claim it — 44x44, border-radius 50%, pointer-events
                                          * auto, shared focus ring, all measured. That is what we want
                                          * here, so unlike `.sbtt-swipeview-stats` it takes NO `:not()`
                                          * exclusion; only the <img> inside needs its own clip.
                                          *
                                          * The image is alt="" because the LINK owns the whole
                                          * accessible name ("@handle on TikTok") — naming both would
                                          * announce the creator twice for one control. */}
                                        {creator && !avatarFailed && (
                                            <a
                                                className="sbtt-swipeview-avatar"
                                                href={creator.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={creator.username
                                                    ? sprintf(
                                                        /* translators: %s: account handle, including the leading @ */
                                                        __('%s on TikTok', 'sb-customizer'),
                                                        `@${creator.username}`
                                                    )
                                                    : __('Open this creator on TikTok', 'sb-customizer')}
                                            >
                                                <img ref={bindAvatarImg} src={creator.src} alt="" />
                                            </a>
                                        )}

                                        {/* ── The rail's action stack (TT-2, TT-3) ──
                                          *
                                          * REPLACES a single `.sbtt-swipeview-stats` cluster —
                                          * one link wrapping all three metrics, with one
                                          * accessible name for the lot — with three separate
                                          * items, each in its own 48px circle and each with its
                                          * own semantics. The design draws them that way and
                                          * TT-3 gives each a different job, which the old
                                          * one-control cluster could not express.
                                          *
                                          * THE SEMANTICS, and why they differ per item. An
                                          * embedded feed has no logged-in visitor, so we cannot
                                          * like, comment or share on their behalf — a heart
                                          * styled like TikTok's own tappable heart would promise
                                          * what the UI cannot deliver. So:
                                          *
                                          *   - LIKES is a readout, not a control: `role="img"`
                                          *     with the exact figure as its name, and
                                          *     `cursor: default`. Nothing happens when you press
                                          *     it, and it does not look like anything should.
                                          *   - COMMENTS is a LINK to the post, where commenting
                                          *     genuinely works.
                                          *   - SHARE is the one real in-page action, and the only
                                          *     one of the three that is a `<button>`.
                                          *
                                          * Each renders only when its metric has data (TT-3 /
                                          * IG-3's "absence is not zero"), and likes vanishes
                                          * ENTIRELY without a count because a heart with no
                                          * number is a meaningless readout — where comments and
                                          * share stay as glyph-only controls, since a link and a
                                          * button still do their job unlabelled. */}
                                        {swipeCounts.likes !== null && (
                                            <div
                                                className="sbtt-swipeview-rail-item sbtt-swipeview-rail-item--readout"
                                                role="img"
                                                aria-label={spokenCount('likes', swipeCounts.likes)}
                                            >
                                                <span className="sbtt-swipeview-rail-ic" aria-hidden="true"><SwipeLikeIcon /></span>
                                                {/* The abbreviation is VISUAL only — the exact
                                                  * figure is already in the name above, and a
                                                  * screen reader gains nothing from "1.2K". */}
                                                <span className="sbtt-swipeview-rail-n" aria-hidden="true">{abbreviateCount(swipeCounts.likes)}</span>
                                            </div>
                                        )}

                                        {permalink && (
                                            <a
                                                className="sbtt-swipeview-rail-item"
                                                href={permalink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={swipeCounts.comments !== null
                                                    ? sprintf(
                                                        /* translators: %s: number of comments, already formatted */
                                                        __('%s comments, view on TikTok', 'sb-customizer'),
                                                        exactCount(swipeCounts.comments)
                                                    )
                                                    : __('View comments on TikTok', 'sb-customizer')}
                                            >
                                                <span className="sbtt-swipeview-rail-ic" aria-hidden="true"><SwipeCommentIcon /></span>
                                                {swipeCounts.comments !== null && (
                                                    <span className="sbtt-swipeview-rail-n" aria-hidden="true">{abbreviateCount(swipeCounts.comments)}</span>
                                                )}
                                            </a>
                                        )}

                                        {/* TikTok exposes a share count, so unlike Instagram
                                          * this glyph carries a number (TT-3). */}
                                        {permalink && (
                                            <button
                                                type="button"
                                                className="sbtt-swipeview-rail-item sb-button-reset"
                                                onClick={share}
                                                aria-label={__('Share', 'sb-customizer')}
                                            >
                                                <span className="sbtt-swipeview-rail-ic" aria-hidden="true"><SwipeShareIcon /></span>
                                                {swipeCounts.shares !== null && (
                                                    <span className="sbtt-swipeview-rail-n" aria-hidden="true">{abbreviateCount(swipeCounts.shares)}</span>
                                                )}
                                            </button>
                                        )}

                                        {/* The touch layout's "View" item, which is where the
                                          * top row's "View on TikTok" pill goes on a phone —
                                          * stated for all three platforms on the walkthrough:
                                          * "the view button on mobile moves at the bottom, the
                                          * mute stays up top".
                                          *
                                          * Rendered unconditionally and hidden by CSS on
                                          * pointer devices rather than gated in JS, because the
                                          * pill and this item are the SAME affordance at two
                                          * viewports. Two JS-gated copies would need a second
                                          * source of truth for "is this a touch device", and
                                          * when the two disagreed (no `matchMedia` at all is the
                                          * real case) the visitor would get both or neither. */}
                                        {permalink && (
                                            <a
                                                className="sbtt-swipeview-rail-item sbtt-swipeview-rail-item--view"
                                                href={permalink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span className="sbtt-swipeview-rail-ic" aria-hidden="true"><SwipeTikTokIcon /></span>
                                                <span className="sbtt-swipeview-rail-n">{__('View', 'sb-customizer')}</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </DialogOverlay>
            </DialogPortal>
        </DialogRoot>
    );
};

export default SwipeView;
